// Code generiert von cmd/protogen aus protokoll/schema.yaml. NICHT VON HAND ÄNDERN.

export const SCHEMA_VERSION = 17;

/** Aufzählung FunkBefund: Wort → Nummer. */
export const FunkBefund = {
  unbekannt: 0,
  stabil: 1,
  kein_verkehr: 2,
  kaum_antworten: 3,
  keine_rueckrufe: 4,
  nichts_angenommen: 5,
  belegung_niedrig: 6,
} as const;
export type FunkBefund = keyof typeof FunkBefund;

/** Aufzählung FunkErkennung: Wort → Nummer. */
export const FunkErkennung = {
  lightweight: 1,
  high_accuracy: 2,
} as const;
export type FunkErkennung = keyof typeof FunkErkennung;

/** Aufzählung FunkmelderLage: Wort → Nummer. */
export const FunkmelderLage = {
  unbekannt: 0,
  bewegung: 1,
  ruhig: 2,
  kalibriert: 3,
  offline: 4,
  stumm: 5,
  rohdaten: 6,
  aus: 7,
} as const;
export type FunkmelderLage = keyof typeof FunkmelderLage;

/** Aufzählung LaufStatus: Wort → Nummer. */
export const LaufStatus = {
  unbekannt: 0,
  laeuft: 1,
  ok: 2,
  fehler: 3,
  abgebrochen: 4,
} as const;
export type LaufStatus = keyof typeof LaufStatus;

/** Aufzählung MeldungArt: Wort → Nummer. */
export const MeldungArt = {
  neu: 1,
  quittiert: 2,
  aktualisiert: 3,
} as const;
export type MeldungArt = keyof typeof MeldungArt;

/** Aufzählung SchwellenQuelle: Wort → Nummer. */
export const SchwellenQuelle = {
  ruhe: 1,
  kalibriert: 2,
  fest: 3,
} as const;
export type SchwellenQuelle = keyof typeof SchwellenQuelle;

/** Aufzählung Schwere: Wort → Nummer. */
export const Schwere = {
  unbekannt: 0,
  info: 1,
  warn: 2,
  error: 3,
  critical: 4,
} as const;
export type Schwere = keyof typeof Schwere;

/** Aufzählung WaechterModus: Wort → Nummer. */
export const WaechterModus = {
  aus: 1,
  melden: 2,
  pausieren: 3,
} as const;
export type WaechterModus = keyof typeof WaechterModus;

/** Aufzählung WaechterStufe: Wort → Nummer. */
export const WaechterStufe = {
  unbekannt: 0,
  ruhig: 1,
  verdacht: 2,
  sicher: 3,
} as const;
export type WaechterStufe = keyof typeof WaechterStufe;

/** Aufzählung WaechterZustand: Wort → Nummer. */
export const WaechterZustand = {
  unbekannt: 0,
  aus: 1,
  ruht: 2,
  wacht: 3,
  stoerung: 4,
} as const;
export type WaechterZustand = keyof typeof WaechterZustand;

/** Aufzählung WlanBand: Wort → Nummer. */
export const WlanBand = {
  "2.4G": 1,
  "5G": 2,
  "6G": 3,
} as const;
export type WlanBand = keyof typeof WlanBand;

/** Nachrichtentyp AgentDeploy. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface AgentDeploy {
  status?: string | null;
  versuch?: number | null;
  erfolg?: number | null;
  fehler?: string | null;
  versuche?: number | null;
  unerreichbar_seit?: number | null;
}

/** Nachrichtentyp AmbientPhase. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface AmbientPhase {
  schluessel?: string | null;
  label?: string | null;
  voreinstellungen?: readonly LichtVoreinstellung[] | null;
  gewaehlt?: number | null;
  helligkeit?: number | null;
  standard_helligkeit?: number | null;
  helligkeit_eigen?: boolean | null;
  start?: LichtGrenze | null;
  start_stunde?: number | null; // h
}

/** Nachrichtentyp BadPhase. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface BadPhase {
  schluessel?: string | null;
  label?: string | null;
  helligkeit?: number | null;
  standard_helligkeit?: number | null;
  helligkeit_eigen?: boolean | null;
  farbtemp?: number | null; // mired
  standard_farbtemp?: number | null; // mired
  farbtemp_eigen?: boolean | null;
  uebergang?: number | null; // s
  start?: LichtGrenze | null;
  start_stunde?: number | null; // h
}

/** Nachrichtentyp DockerGpu. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface DockerGpu {
  prozent?: number | null; // %
  speicher?: number | null; // B
  motoren?: readonly DockerMotor[] | null;
}

/** Nachrichtentyp DockerMotor. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface DockerMotor {
  name?: string | null;
  prozent?: number | null; // %
}

/** Nachrichtentyp DockerRechner. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface DockerRechner {
  id?: string | null;
  name?: string | null;
  online?: boolean | null;
}

/** Nachrichtentyp ErkennerStelle. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface ErkennerStelle {
  p?: number | null; // 1
  x1?: number | null; // 1
  y1?: number | null; // 1
  x2?: number | null; // 1
  y2?: number | null; // 1
}

/** Nachrichtentyp FunkDiagnose. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface FunkDiagnose {
  am?: number | null;
  erzeugt?: number | null; // 1/s
  gesendet?: number | null; // 1/s
  empfangen?: number | null; // 1/s
  rueckrufe?: number | null; // 1/s
  angenommen?: number | null; // 1/s
  verwertet?: number | null; // 1/s
  verworfen?: number | null; // 1/s
  fehlend?: number | null; // 1/s
  ueberzaehlig?: number | null; // 1/s
  veraltet?: number | null; // 1/s
  unsortiert?: number | null; // 1/s
  belegung?: number | null; // %
  kanal?: number | null;
  signal?: number | null; // dBm
  befund?: FunkBefund | null;
}

/** Nachrichtentyp FunkEinstellungen. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface FunkEinstellungen {
  erkennung?: FunkErkennung | null;
  treffer_an?: number | null;
  treffer_aus?: number | null;
  haltezeit?: number | null; // s
  led?: boolean | null;
}

/** Nachrichtentyp FunkRolle. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface FunkRolle {
  id?: string | null;
  aus?: boolean | null;
  abschaltbar?: boolean | null;
}

/** Nachrichtentyp GeraetEntitaet. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface GeraetEntitaet {
  slug?: string | null;
  name?: string | null;
  art?: string | null;
  einheit?: string | null;
  zustand?: string | null;
  klasse?: string | null;
  ext_id?: string | null;
  aktualisiert?: number | null;
}

/** Nachrichtentyp GrowboxPort. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface GrowboxPort {
  port?: number | null;
  name?: string | null;
  art?: string | null;
  stufe?: number | null;
  stufe_an?: number | null;
  stufe_aus?: number | null;
  an?: boolean | null;
  modus?: number | null;
  ueberschuss?: number | null;
}

/** Nachrichtentyp GuteSchwelle. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface GuteSchwelle {
  schwelle?: number | null; // 1
  quelle?: SchwellenQuelle | null;
  am?: number | null;
}

/** Nachrichtentyp HostAnomalie. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface HostAnomalie {
  host?: string | null;
  metrik?: string | null;
  aktuell?: number | null;
  mittel?: number | null;
  z?: number | null;
}

/** Nachrichtentyp HostAufenthalt. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface HostAufenthalt {
  adresse?: string | null;
  eingetragen?: string | null;
  vom_agenten?: boolean | null;
  ort?: string | null;
  netz?: string | null;
  gemeldet?: number | null;
}

/** Nachrichtentyp HostCluster. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface HostCluster {
  id?: string | null;
  name?: string | null;
}

/** Nachrichtentyp HostUpdates. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface HostUpdates {
  offen?: number | null;
  zurueckgehalten?: number | null;
  sicherheit?: number | null;
  neustart?: boolean | null;
  uu_aktiv?: boolean | null;
  apt_zuletzt?: number | null; // s
  zeit?: number | null;
}

/** Nachrichtentyp Integration. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface Integration {
  id?: string | null;
  name?: string | null;
  status?: string | null;
  detail?: string | null;
  zuletzt?: number | null; // s
  geraete?: number | null;
  geraete_online?: number | null;
  gesamt?: string | null;
}

/** Nachrichtentyp KalibrierAuftrag. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface KalibrierAuftrag {
  seit?: number | null;
  bis?: number | null;
  laeuft?: boolean | null;
  grund?: string | null;
}

/** Nachrichtentyp LaufFortschritt. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface LaufFortschritt {
  bytes?: string | null;
  pct?: string | null;
  speed?: string | null;
  eta?: string | null;
  aktualisiert?: number | null;
}

/** Nachrichtentyp LernAbschnitt. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface LernAbschnitt {
  von_min?: number | null; // min
  bis_min?: number | null; // min
  helligkeit?: number | null;
}

/** Nachrichtentyp LernKurvenPunkt. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface LernKurvenPunkt {
  minute?: number | null; // min
  helligkeit?: number | null;
  sicher?: boolean | null;
  gefuellt?: boolean | null;
  vorlaeufig?: number | null;
}

/** Nachrichtentyp LernPunkt. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface LernPunkt {
  minute?: number | null; // min
  helligkeit?: number | null;
  bestaetigt?: boolean | null;
}

/** Nachrichtentyp LichtGrenze. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface LichtGrenze {
  sonne?: string | null;
  versatz_min?: number | null; // min
  uhrzeit?: string | null;
}

/** Nachrichtentyp LichtVoreinstellung. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface LichtVoreinstellung {
  name?: string | null;
  farben?: readonly string[] | null;
}

/** Nachrichtentyp LichtZiel. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface LichtZiel {
  topic?: string | null;
  typ?: string | null;
}

/** Nachrichtentyp NetzGeraet. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface NetzGeraet {
  name?: string | null;
  ip?: string | null;
  mac?: string | null;
  rolle?: string | null;
  verbunden_mit?: string | null;
  online?: boolean | null;
}

/** Nachrichtentyp NetzHost. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface NetzHost {
  id?: string | null;
  name?: string | null;
  ip?: string | null;
  mac?: string | null;
  rolle?: string | null;
  schlafen_ok?: boolean | null;
  os?: string | null;
  temp_grenze?: number | null; // °C
  online?: boolean | null;
  noch_nicht_in_betrieb?: boolean | null;
}

/** Nachrichtentyp OpenwrtTailscale. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface OpenwrtTailscale {
  online?: boolean | null;
  ipv4?: string | null;
  backend?: string | null;
  empfang?: number | null; // Mbit/s
  senden?: number | null; // Mbit/s
}

/** Nachrichtentyp OpenwrtTunnel. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface OpenwrtTunnel {
  name?: string | null;
  oben?: boolean | null;
  laufzeit?: number | null; // s
  ipv4?: string | null;
  ipv6?: string | null;
}

/** Nachrichtentyp RekeyFenster. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface RekeyFenster {
  host?: string | null;
  bis?: number | null;
  geoeffnet?: number | null;
  von?: string | null;
  grund?: string | null;
}

/** Nachrichtentyp SensorWert. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface SensorWert {
  name?: string | null;
  wert?: number | null;
  zeit?: number | null;
}

/** Nachrichtentyp SpeicherErsatz. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface SpeicherErsatz {
  platte?: string | null;
  seriennummer?: string | null;
  modell?: string | null;
  groesse?: number | null; // B
  hat_daten?: boolean | null;
  partitionen?: number | null;
  signaturen?: readonly string[] | null;
  mitglied?: string | null;
  mitglied_label?: string | null;
  smart_warnung?: string | null;
}

/** Nachrichtentyp SpeicherMitglied. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface SpeicherMitglied {
  name?: string | null;
  pfad?: string | null;
  guid?: string | null;
  zustand?: string | null;
  lesefehler?: string | null;
  schreibfehler?: string | null;
  pruefsummenfehler?: string | null;
  gruppe?: string | null;
  resilver?: boolean | null;
  notiz?: string | null;
  platte?: string | null;
  seriennummer?: string | null;
  label?: string | null;
}

/** Nachrichtentyp SpeicherScan. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface SpeicherScan {
  art?: string | null;
  laeuft?: boolean | null;
  prozent?: number | null; // %
  rest?: string | null;
  tempo?: string | null;
  menge?: string | null;
  text?: string | null;
}

/** Nachrichtentyp Strom. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface Strom {
  throttled_now?: number | null; // bits
  throttled_seen?: number | null; // bits
  volt_core?: number | null; // V
  throttled_hex?: string | null;
}

/** Nachrichtentyp SzenenZiel. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface SzenenZiel {
  topic?: string | null;
  typ?: string | null;
  payload?: string | null;
}

/** Nachrichtentyp TopoKante. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface TopoKante {
  id?: string | null;
  quelle?: string | null;
  ziel?: string | null;
  art?: string | null;
  label?: string | null;
}

/** Nachrichtentyp TopoLink. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface TopoLink {
  gesundheit?: string | null;
  iface?: string | null;
  flaps?: number | null;
  letzter_flap?: string | null;
  ausfall?: boolean | null;
}

/** Nachrichtentyp TopoPruefkante. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface TopoPruefkante {
  id?: string | null;
  quelle?: string | null;
  ziel?: string | null;
  blick?: string | null;
  gesundheit?: string | null;
  label?: string | null;
}

/** Nachrichtentyp TopoPruefung. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface TopoPruefung {
  blick?: string | null;
  gesundheit?: string | null;
  http_status?: number | null;
  latenz?: number | null; // ms
  url?: string | null;
  notiz?: string | null;
  zuletzt?: string | null;
}

/** Nachrichtentyp WaechterEinstellung. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface WaechterEinstellung {
  modus?: WaechterModus | null;
  empfindlichkeit?: number | null; // 1
  schwelle_niedrig?: number | null; // 1
  schwelle_hoch?: number | null; // 1
  steigerung?: number | null; // 1
  schonzeit_min?: number | null; // min
  ohne_meinung?: boolean | null;
  modell?: string | null;
}

/** Nachrichtentyp WaechterGlaettung. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface WaechterGlaettung {
  ewm?: number | null; // 1
  kurz?: number | null; // 1
  lang?: number | null; // 1
  takte?: number | null;
  takte_gesamt?: number | null;
  p?: number | null; // 1
}

/** Nachrichtentyp WaechterMeinung. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface WaechterMeinung {
  urteil?: string | null;
  art?: string | null;
  begruendung?: string | null;
  modell?: string | null;
  ms?: number | null; // ms
  fehler?: string | null;
  ausschnitt?: boolean | null;
}

/** Nachrichtentyp WaechterSicht. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface WaechterSicht {
  zeit?: number | null;
  stellen?: readonly ErkennerStelle[] | null;
  breite?: number | null; // px
  hoehe?: number | null; // px
  p?: number | null; // 1
  bewertet?: boolean | null;
  hinweis?: string | null;
}

/** Nachrichtentyp WanRouter. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface WanRouter {
  host?: string | null;
  name?: string | null;
  cluster?: string | null;
  cluster_name?: string | null;
  quelle?: string | null;
  status?: string | null;
  seit?: number | null;
  zuletzt?: number | null;
  stumm?: boolean | null;
  wan_oben?: boolean | null;
  ipv4?: string | null;
  ipv6?: string | null;
  empfang?: number | null; // Mbit/s
  senden?: number | null; // Mbit/s
}

/** Nachrichtentyp Wlan. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface Wlan {
  clients?: number | null;
  bands?: readonly WlanBandZahl[] | null;
}

/** Nachrichtentyp WlanBandZahl. Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface WlanBandZahl {
  band?: WlanBand | null;
  clients?: number | null;
}

/** Laufende Werte eines Rechners (Agent über MQTT homelab/stats/+) (Topic host.stats, Nr 1). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface HostStats {
  cpu?: number | null; // %
  cpu_temp?: number | null; // °C
  mem_pct?: number | null; // %
  uptime_s?: number | null; // s
  power?: Strom | null;
  wifi?: Wlan | null;
  adressen?: readonly string[] | null;
}

/** Bewegung aus dem WLAN-Kanal eines ESP32-Boards (Topic funkmelder.bewegung, Nr 2). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface FunkmelderBewegung {
  lage?: FunkmelderLage | null;
  seit?: number | null;
  wert?: number | null; // 1
  schwelle?: number | null; // 1
  gemessen_am?: number | null;
  kategorie?: string | null;
  einstellungen?: FunkEinstellungen | null;
  diagnose?: FunkDiagnose | null;
  rohdaten?: boolean | null;
  gute_schwelle?: GuteSchwelle | null;
  auftrag?: KalibrierAuftrag | null;
  aus?: boolean | null;
  abschaltbar?: boolean | null;
}

/** Gespeicherte Meldung neu oder quittiert (audit.Aenderung) (Topic meldung, Nr 3). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface Meldung {
  art?: MeldungArt | null;
  domain?: string | null;
  type?: string | null;
  severity?: Schwere | null;
  entity?: string | null;
  summary?: string | null;
  zeit?: number | null;
}

/** Feinstaub (Sensirion SPS30) eines Boards mit eigener Protokoll-Komponente (Topic feinstaub.luft, Nr 4). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface FeinstaubLuft {
  pm1_0?: number | null; // µg/m³
  pm2_5?: number | null; // µg/m³
  pm4_0?: number | null; // µg/m³
  pm10_0?: number | null; // µg/m³
  partikel_0_5?: number | null; // #/cm³
  partikel_2_5?: number | null; // #/cm³
  partikelgroesse?: number | null; // µm
}

/** Stand des Druckerwächters (Zustand, Bewertung, letzte Sicht, Einstellung) (Topic druckwaechter.stand, Nr 5). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface DruckwaechterStand {
  modus?: WaechterModus | null;
  zustand?: WaechterZustand | null;
  grund?: string | null;
  seit?: number | null;
  datei?: string | null;
  stufe?: WaechterStufe | null;
  wert?: number | null; // 1
  glaettung?: WaechterGlaettung | null;
  verlauf?: readonly number[] | null; // 1
  schonzeit?: number | null;
  stumm?: boolean | null;
  bilder_je_s?: number | null; // 1/s
  erkenner_ms?: number | null; // ms
  erkenner?: string | null;
  takt_s?: number | null; // s
  sicht?: WaechterSicht | null;
  fragt?: boolean | null;
  letzte_frage?: WaechterMeinung | null;
  offener_fall?: string | null;
  letzter_fall?: string | null;
  einstellung?: WaechterEinstellung | null;
}

/** Zustand eines Erkenner-Dienstes (Modell geladen, Bilder, Zeit je Bild) (Topic erkenner.zustand, Nr 6). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface ErkennerZustand {
  bereit?: boolean | null;
  modell?: string | null;
  threads?: number | null;
  gestartet?: number | null;
  bilder?: number | null;
  ms_letztes?: number | null; // ms
  ms_mittel?: number | null; // ms
  letzte_anfrage?: number | null;
  fehler?: number | null;
}

/** Letzter Lauf einer Aufgabe (Status, Zeiten, Fehler, Fortschritt) und ihr naechster Termin (Topic task.lauf, Nr 7). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface TaskLauf {
  run?: string | null;
  status?: LaufStatus | null;
  start?: number | null;
  ende?: number | null;
  dauer_ms?: number | null; // ms
  fehler?: string | null;
  naechster?: number | null;
  typische_dauer_ms?: number | null; // ms
  fortschritt?: LaufFortschritt | null;
}

/** Ein Gerät der Geräte-Seite (Zigbee, Z-Wave, ESPHome): Stammdaten, Erreichbarkeit, Entitäten mit Zustand (Topic geraet.stand, Nr 8). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface GeraetStand {
  name?: string | null;
  label?: string | null;
  bereich?: string | null;
  art?: string | null;
  hersteller?: string | null;
  modell?: string | null;
  integration?: string | null;
  integration_slug?: string | null;
  adresse?: string | null;
  online?: boolean | null;
  zuletzt?: number | null;
  lqi?: number | null;
  stilllegung_domain?: string | null;
  stilllegung_id?: string | null;
  versorgt?: string | null;
  aus_gesperrt?: boolean | null;
  funkmelder?: FunkRolle | null;
  entitaeten?: readonly GeraetEntitaet[] | null;
}

/** Ein ZFS-Pool (Kapazität, Zustand, Mitglieder, Scan, Ersatzplatten) (Topic speicher.pool, Nr 9). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface SpeicherPool {
  host?: string | null;
  host_id?: string | null;
  cluster?: string | null;
  name?: string | null;
  groesse?: number | null; // B
  belegt?: number | null; // B
  frei?: number | null; // B
  frag?: string | null;
  zustand?: string | null;
  nutzbar_groesse?: number | null; // B
  nutzbar_belegt?: number | null; // B
  nutzbar_frei?: number | null; // B
  overhead?: number | null; // B
  mitglieder?: readonly SpeicherMitglied[] | null;
  scan?: SpeicherScan | null;
  status_text?: string | null;
  action_text?: string | null;
  ersatz?: readonly SpeicherErsatz[] | null;
}

/** Eine Platte (Stammdaten, Zustand, Temperatur, SMART-Werte, Selbsttest) ohne das volle smartctl-JSON (Topic speicher.platte, Nr 10). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface SpeicherPlatte {
  host?: string | null;
  host_id?: string | null;
  cluster?: string | null;
  name?: string | null;
  groesse?: number | null; // B
  rotierend?: boolean | null;
  anschluss?: string | null;
  modell?: string | null;
  zustand?: string | null;
  temp?: string | null;
  pool?: string | null;
  nvme?: boolean | null;
  seriennummer?: string | null;
  firmware?: string | null;
  betriebsstunden?: number | null; // h
  geschrieben?: number | null; // B
  verschleiss?: number | null; // %
  umgelagert?: number | null;
  schwebend?: number | null;
  offline_unkorrigierbar?: number | null;
  gemeldet_unkorrigierbar?: number | null;
  crc_fehler?: number | null;
  selbsttest_status?: string | null;
  selbsttest_ok?: boolean | null;
  selbsttest_vor_h?: number | null; // h
  selbsttest_abbruch?: string | null;
  selbsttest_abbruch_vor_h?: number | null; // h
  selbsttest_laeuft?: boolean | null;
  selbsttest_rest?: number | null; // %
  prefail?: string | null;
  partitionen?: number | null;
  signaturen?: readonly string[] | null;
  belegt?: boolean | null;
}

/** Ein ZFS-Dataset (belegt, verfügbar, referenziert) (Topic speicher.dataset, Nr 11). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface SpeicherDataset {
  host?: string | null;
  cluster?: string | null;
  name?: string | null;
  belegt?: number | null; // B
  verfuegbar?: number | null; // B
  referenziert?: number | null; // B
  mountpoint?: string | null;
}

/** Ein Nicht-ZFS-Dateisystem (df-Sicht) (Topic speicher.dateisystem, Nr 12). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface SpeicherDateisystem {
  host?: string | null;
  cluster?: string | null;
  quelle?: string | null;
  mountpoint?: string | null;
  groesse?: number | null; // B
  belegt?: number | null; // B
  verfuegbar?: number | null; // B
}

/** Lage des Speicher-Collectors (letzter Lauf, Standard-Schwellen) (Topic speicher.lage, Nr 13). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface SpeicherLage {
  zuletzt?: number | null;
  ok?: boolean | null;
  warn_pct?: number | null; // %
  krit_pct?: number | null; // %
}

/** Eine Bewegungslicht-Regel (bathroom_light) mit Phasen, Auslösern und lernender Helligkeit (Topic licht.bad, Nr 14). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface LichtBad {
  name?: string | null;
  aktiv?: boolean | null;
  an?: boolean | null;
  manuell?: boolean | null;
  quelle?: string | null;
  lampe?: string | null;
  phase?: string | null;
  phase_label?: string | null;
  phasen?: readonly BadPhase[] | null;
  sonnenaufgang?: number | null; // h
  sonnenuntergang?: number | null; // h
  naechste_phase?: string | null;
  naechste_phase_um?: number | null;
  abend_ende?: number | null; // h
  bewegung_topic?: string | null;
  tuer_topic?: string | null;
  dimmer_topic?: string | null;
  schalter_topics?: readonly string[] | null;
  bewegung_frist?: number | null; // s
  tuer_verzug?: number | null; // s
  lernen?: boolean | null;
  lernen_folgen?: boolean | null;
  lern_anzahl?: number | null;
  lern_proben?: readonly LernPunkt[] | null;
  lern_kurve?: readonly LernKurvenPunkt[] | null;
  lern_abschnitte?: readonly LernAbschnitt[] | null;
  jetzt_helligkeit?: number | null;
  jetzt_sicher?: boolean | null;
  jetzt_vorlaeufig?: number | null;
  wunsch?: LernPunkt | null;
}

/** Eine Ambient-Regel (ambient_group) mit Phasen, Voreinstellungen, Lampen und lernender Helligkeit (Topic licht.ambient, Nr 15). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface LichtAmbient {
  name?: string | null;
  aktiv?: boolean | null;
  an?: boolean | null;
  phase?: string | null;
  phase_label?: string | null;
  phasen?: readonly AmbientPhase[] | null;
  szene?: string | null;
  sonnenaufgang?: number | null; // h
  sonnenuntergang?: number | null; // h
  naechste_phase?: string | null;
  naechste_phase_um?: number | null;
  ziele?: readonly LichtZiel[] | null;
  uebergang?: number | null; // s
  uebergang_standard?: number | null; // s
  uebergang_max?: number | null; // s
  abend_ende?: number | null; // h
  lernen?: boolean | null;
  lernen_folgen?: boolean | null;
  lern_anzahl?: number | null;
  lern_proben?: readonly LernPunkt[] | null;
  lern_kurve?: readonly LernKurvenPunkt[] | null;
  lern_abschnitte?: readonly LernAbschnitt[] | null;
  jetzt_helligkeit?: number | null;
  jetzt_sicher?: boolean | null;
  jetzt_vorlaeufig?: number | null;
  wunsch?: LernPunkt | null;
}

/** Eine Szene (scene) mit ihren Zielen (Topic licht.szene, Nr 16). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface LichtSzene {
  name?: string | null;
  aktiv?: boolean | null;
  an?: boolean | null;
  go_engine?: boolean | null;
  ambient_zurueck?: string | null;
  ziel_anzahl?: number | null;
  farben?: readonly string[] | null;
  ziele?: readonly SzenenZiel[] | null;
}

/** Ein Agent des Inventars (Edge-Komponente) mit Stammdaten, abgeleitetem Status, Takt-Messung und Deploy-Zustand (Topic agent.stand, Nr 17). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface AgentStand {
  name?: string | null;
  art?: string | null;
  mqtt_topic?: string | null;
  lebenszeichen_s?: number | null; // s
  daten_s?: number | null; // s
  max_stille_s?: number | null; // s
  ssh_alias?: string | null;
  aktiv?: boolean | null;
  notiz?: string | null;
  angelegt?: string | null;
  geaendert?: string | null;
  status?: string | null;
  zuletzt?: number | null;
  abgemeldet_seit?: number | null;
  deploy?: AgentDeploy | null;
  gemessen_s?: number | null; // s
  proben?: number | null;
  abweichung?: number | null; // %
  soll_s?: number | null; // s
}

/** Ein beobachtetes Topic, das noch kein Agent ist (zum Übernehmen oder Verwerfen) (Topic agent.kandidat, Nr 18). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface AgentKandidat {
  topic?: string | null;
  art?: string | null;
  name?: string | null;
  frames?: number | null;
  erstmals?: string | null;
  zuletzt?: string | null;
}

/** Eine Steckdose oder ein Zähler (Z-Wave oder Zigbee) mit Schalter, Leistung, Zählerstand und Lebenszeichen (Topic steckdose.stand, Nr 19). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface SteckdoseStand {
  quelle?: string | null;
  name?: string | null;
  knoten?: number | null;
  schalter?: boolean | null;
  hat_schalter?: boolean | null;
  watt?: number | null; // W
  kwh?: number | null; // kWh
  volt?: number | null; // V
  ampere?: number | null; // A
  online?: boolean | null;
  kwh_gesamt?: number | null; // kWh
  zuletzt?: number | null;
  gemessen?: number | null;
}

/** Ein AC-Infinity-Controller mit Messwerten, Ports und aktiver Pflanzphase (Topic growbox.stand, Nr 20). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface GrowboxStand {
  name?: string | null;
  mac?: string | null;
  online?: boolean | null;
  temperatur?: number | null; // °C
  feuchte?: number | null; // %
  vpd?: number | null; // kPa
  ports?: readonly GrowboxPort[] | null;
  phase?: string | null;
  position?: number | null;
}

/** Zustand des AC-Infinity-Zugangs (eingerichtet, angemeldet, Phasen, letzter Abruf) (Topic growbox.lage, Nr 21). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface GrowboxLage {
  eingerichtet?: boolean | null;
  angemeldet?: boolean | null;
  phasen?: readonly string[] | null;
  letzter_abruf?: number | null;
  email?: string | null;
}

/** Ein Sensor (Klima, CO2-Monitor, LiPo-Wächter oder Wassermelder) mit seinen letzten Werten (Topic sensor.stand, Nr 22). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface SensorStand {
  quelle?: string | null;
  name?: string | null;
  temperatur?: number | null; // °C
  feuchte?: number | null; // %
  druck?: number | null; // hPa
  batterie?: number | null; // %
  funk?: number | null;
  bewegung?: boolean | null;
  kontakt?: boolean | null;
  kontakt_seit?: number | null;
  wasser?: boolean | null;
  status?: string | null;
  status_zeit?: number | null;
  werte?: readonly SensorWert[] | null;
  zuletzt?: number | null;
}

/** Ein Sensor der Klima-Übersicht mit Messwerten, Zustand und Grund (Topic klima.bewertung, Nr 23). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface KlimaBewertung {
  domain?: string | null;
  sensor?: string | null;
  zeit?: string | null;
  werte?: readonly SensorWert[] | null;
  batterie?: number | null; // %
  status?: string | null;
  grund?: string | null;
  aus?: boolean | null;
  seit?: string | null;
  aussen?: boolean | null;
  warn_nach?: number | null; // s
}

/** Ein Cluster mit seinen Rechnern und Netzgeräten und ob sie erreichbar sind (Topic netz.cluster, Nr 24). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface NetzCluster {
  id?: string | null;
  name?: string | null;
  subnetz?: string | null;
  hosts?: readonly NetzHost[] | null;
  geraete?: readonly NetzGeraet[] | null;
  position?: number | null;
}

/** WAN-Durchsatz, Tunnel und Tailscale eines OpenWrt-Routers (Topic openwrt.stand, Nr 25). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface OpenwrtStand {
  router?: string | null;
  host?: string | null;
  topic?: string | null;
  wan?: string | null;
  empfang?: number | null; // Mbit/s
  senden?: number | null; // Mbit/s
  tunnel?: readonly OpenwrtTunnel[] | null;
  tailscale?: OpenwrtTailscale | null;
  zuletzt?: number | null;
}

/** Zustand einer Netzwerkschnittstelle eines Rechners mit Ausfall und Flaps (Topic link.stand, Nr 26). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface LinkStand {
  entity?: string | null;
  host?: string | null;
  iface?: string | null;
  zustand?: string | null;
  gesundheit?: string | null;
  seit?: string | null;
  ausfall_seit?: string | null;
  ausfall?: boolean | null;
  flaps?: number | null;
  letzter_flap?: string | null;
  letztes_ereignis?: string | null;
}

/** Ein Knoten der Topologie (Router, Cluster, Rechner) mit Zustand, Verbindung und Prüfungen (Topic topologie.knoten, Nr 27). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface TopologieKnoten {
  id?: string | null;
  label?: string | null;
  art?: string | null;
  eltern?: string | null;
  cluster?: string | null;
  rolle?: string | null;
  ip?: string | null;
  os?: string | null;
  status?: string | null;
  link?: TopoLink | null;
  pruefungen?: readonly TopoPruefung[] | null;
  position?: number | null;
}

/** Die Kanten der Topologie und die Kanten der Uptime-Prüfungen (Topic topologie.kanten, Nr 28). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface TopologieKanten {
  kanten?: readonly TopoKante[] | null;
  pruefkanten?: readonly TopoPruefkante[] | null;
}

/** Offene Rekey-Fenster und der Zustand der Integrationen (MQTT, zigbee2mqtt, Z-Wave, ESPHome) (Topic netz.lage, Nr 29). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface NetzLage {
  rekey?: readonly RekeyFenster[] | null;
  integrationen?: readonly Integration[] | null;
}

/** Internet hinter forge (eigene Probe) und je Router, was sein Agent meldet (Topic wan.lage, Nr 30). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface WanLage {
  ok?: boolean | null;
  status?: string | null;
  fehlversuche?: number | null;
  geprueft?: number | null;
  erfolg?: number | null;
  fehlschlag?: number | null;
  fehler?: string | null;
  ziel?: string | null;
  seit?: number | null;
  ipv4?: string | null;
  ipv6?: string | null;
  empfang?: number | null; // Mbit/s
  senden?: number | null; // Mbit/s
  router?: string | null;
  routers?: readonly WanRouter[] | null;
}

/** Ein Rechner mit Eintrag, online seit, Updates, SSH und Aufenthalt (Topic host.stand, Nr 31). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface HostStand {
  id?: string | null;
  name?: string | null;
  ip?: string | null;
  mac?: string | null;
  rolle?: string | null;
  os?: string | null;
  schlafen_ok?: boolean | null;
  temp_grenze?: number | null; // °C
  neustart_prio?: number | null;
  neustart_tag?: number | null;
  neustart_nach?: readonly string[] | null;
  quelle?: string | null;
  online?: boolean | null;
  seit?: number | null;
  offline_24h?: number | null;
  updates?: HostUpdates | null;
  ssh_ok?: boolean | null;
  ssh_fehler?: string | null;
  ssh_adresse?: string | null;
  noch_nicht_in_betrieb?: boolean | null;
  aufenthalt?: HostAufenthalt | null;
  cluster?: string | null;
  position?: number | null;
}

/** Cluster-Reihenfolge, Anomalien gegen die Baseline und Rechner, die offline sein dürfen (Topic host.lage, Nr 32). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface HostLage {
  cluster?: readonly HostCluster[] | null;
  anomalien?: readonly HostAnomalie[] | null;
  offline_ok?: readonly string[] | null;
  verlauf_host?: string | null;
}

/** Ein Zigbee-Aktor mit Schaltzustand, Helligkeit, Farbe, Erreichbarkeit und Funk (Topic aktor.stand, Nr 33). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface AktorStand {
  name?: string | null;
  art?: string | null;
  zustand?: string | null;
  helligkeit?: number | null;
  farbtemperatur?: number | null; // mired
  farbe?: string | null;
  zuletzt?: number | null;
  erreichbar?: boolean | null;
  batterie?: number | null; // %
  funk?: number | null;
}

/** Ein Docker-Container mit Zustand, Image, Ports, CPU, Speicher und Grafik (Topic docker.stand, Nr 34). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface DockerStand {
  rechner?: string | null;
  name?: string | null;
  image?: string | null;
  status?: string | null;
  zustand?: string | null;
  ports?: string | null;
  stack?: string | null;
  cpu?: number | null; // %
  speicher?: number | null; // B
  speicher_grenze?: number | null; // B
  speicher_anteil?: number | null; // %
  gpu?: DockerGpu | null;
  position?: number | null;
}

/** Die Rechner mit Docker in ihrer Reihenfolge und ob sie antworten (Topic docker.lage, Nr 35). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface DockerLage {
  rechner?: readonly DockerRechner[] | null;
}

/** Ein Teil des Argus-Stands (Feld der obersten Ebene) als JSON (Topic argus.teil, Nr 36). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface ArgusTeil {
  json?: string | null;
}

/** Ein Lauf des Argus-Trainings als JSON (Topic argus.lauf, Nr 37). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface ArgusLauf {
  json?: string | null;
}

/** Ein großer Teil des Argus-Stands, nur für die Seiten, die ihn zeigen, als JSON (Topic argus.gross, Nr 38). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface ArgusGross {
  json?: string | null;
}

/** Ein überwachtes Ziel aus einem Blickwinkel (extern/intern) mit Zustand, Antwortzeit und Zertifikat (Topic uptime.stand, Nr 39). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface UptimeStand {
  entity?: string | null;
  ziel_id?: string | null;
  bezeichnung?: string | null;
  blickwinkel?: string | null;
  url?: string | null;
  zustand?: string | null;
  ok?: boolean | null;
  http_status?: number | null;
  latenz?: number | null; // ms
  seit?: number | null;
  fehlversuche?: number | null;
  wackler?: number | null;
  geprueft?: number | null;
  fehler?: string | null;
  zertifikat_bis?: number | null;
  zertifikat_tage?: number | null; // d
  position?: number | null;
}

/** Entprellter Zustand des DNS-Resolvers (AdGuard) mit Server, Betriebsart und Antwortzeit (Topic dns.lage, Nr 40). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface DnsLage {
  ok?: boolean | null;
  zustand?: string | null;
  server?: string | null;
  modus?: string | null;
  seit?: number | null;
  wackler?: number | null;
  latenz?: number | null; // ms
}

/** Einstellung des Local-API-Bypass (an/aus, Auto-Aus in Minuten, seit wann) (Topic bypass.lage, Nr 41). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface BypassLage {
  an?: boolean | null;
  auto_aus_min?: number | null; // min
  an_seit?: number | null;
}

/** Stand von Claude ohne Chats und Repo-Stände (verbunden, Verbrauch, Modelle …), dazu die Reihenfolge der Chats und Repo-Stände, als JSON (Topic claude.lage, Nr 42). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface ClaudeLage {
  json?: string | null;
}

/** Ein Claude-Chat in der freien Sicht ohne seine Commits, als JSON (Topic claude.chat, Nr 43). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface ClaudeChat {
  json?: string | null;
}

/** Die Commits eines Claude-Chats (Trailer Homelab-Chat), jüngste zuerst, als JSON (Topic claude.commits, Nr 44). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface ClaudeCommits {
  json?: string | null;
}

/** Git-Stand eines Repos (offene Dateien, nicht gepushte Commits, Verlauf), als JSON (Topic claude.repo, Nr 45). Fehlt ein Feld, ist es unbekannt; null = jetzt unbekannt. */
export interface ClaudeRepo {
  json?: string | null;
}

export type Art = "state" | "event" | "command";

export interface FeldInfo {
  readonly nr: number;
  readonly name: string;
  readonly typ: string;
  readonly einheit?: string;
  readonly skala?: number;
  readonly totband?: number;
}

export interface TopicInfo {
  readonly nr: number;
  readonly name: string;
  readonly art: Art;
  readonly muster: string;
  readonly beschreibung: string;
  readonly rate?: string;
  /** Lebenszeichen eines state-Topics in Millisekunden (0 = keins verlangt). */
  readonly lebenszeichen_ms: number;
  readonly lesen: readonly string[];
  readonly schreiben: readonly string[];
  readonly felder: readonly FeldInfo[];
  readonly ziel?: string;
}

/** Alle Topic-Familien, nach Nummer sortiert. */
export const TOPICS: readonly TopicInfo[] = [
  {
    nr: 1,
    name: "host.stats",
    art: "state",
    muster: "host.{host}.stats",
    beschreibung: "Laufende Werte eines Rechners (Agent über MQTT homelab/stats/+)",
    rate: "normal",
    lebenszeichen_ms: 30000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "cpu", typ: "u8", einheit: "%", totband: 2.0 },
      { nr: 2, name: "cpu_temp", typ: "i16", einheit: "°C", totband: 1.0 },
      { nr: 3, name: "mem_pct", typ: "u8", einheit: "%", totband: 1.0 },
      { nr: 4, name: "uptime_s", typ: "u32", einheit: "s", totband: 60.0 },
      { nr: 5, name: "power", typ: "Strom" },
      { nr: 6, name: "wifi", typ: "Wlan" },
      { nr: 7, name: "adressen", typ: "[string]" },
    ],
  },
  {
    nr: 2,
    name: "funkmelder.bewegung",
    art: "state",
    muster: "funkmelder.{id}.bewegung",
    beschreibung: "Bewegung aus dem WLAN-Kanal eines ESP32-Boards",
    rate: "live",
    lebenszeichen_ms: 60000,
    lesen: ["nutzer"],
    schreiben: ["backend", "geraet"],
    felder: [
      { nr: 1, name: "lage", typ: "FunkmelderLage" },
      { nr: 2, name: "seit", typ: "zeit" },
      { nr: 3, name: "wert", typ: "u16", einheit: "1", skala: 0.0001 },
      { nr: 4, name: "schwelle", typ: "u16", einheit: "1", skala: 0.0001 },
      { nr: 5, name: "gemessen_am", typ: "zeit" },
      { nr: 6, name: "kategorie", typ: "string" },
      { nr: 7, name: "einstellungen", typ: "FunkEinstellungen" },
      { nr: 8, name: "diagnose", typ: "FunkDiagnose" },
      { nr: 9, name: "rohdaten", typ: "bool" },
      { nr: 10, name: "gute_schwelle", typ: "GuteSchwelle" },
      { nr: 11, name: "auftrag", typ: "KalibrierAuftrag" },
      { nr: 12, name: "aus", typ: "bool" },
      { nr: 13, name: "abschaltbar", typ: "bool" },
    ],
  },
  {
    nr: 3,
    name: "meldung",
    art: "event",
    muster: "meldung",
    beschreibung: "Gespeicherte Meldung neu oder quittiert (audit.Aenderung)",
    lebenszeichen_ms: 0,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "art", typ: "MeldungArt" },
      { nr: 2, name: "domain", typ: "string" },
      { nr: 3, name: "type", typ: "string" },
      { nr: 4, name: "severity", typ: "Schwere" },
      { nr: 5, name: "entity", typ: "string" },
      { nr: 6, name: "summary", typ: "string" },
      { nr: 7, name: "zeit", typ: "zeit_ms" },
    ],
  },
  {
    nr: 4,
    name: "feinstaub.luft",
    art: "state",
    muster: "feinstaub.{id}.luft",
    beschreibung: "Feinstaub (Sensirion SPS30) eines Boards mit eigener Protokoll-Komponente",
    rate: "normal",
    lebenszeichen_ms: 120000,
    lesen: ["nutzer"],
    schreiben: ["geraet"],
    felder: [
      { nr: 1, name: "pm1_0", typ: "u16", einheit: "µg/m³", skala: 0.1, totband: 0.5 },
      { nr: 2, name: "pm2_5", typ: "u16", einheit: "µg/m³", skala: 0.1, totband: 0.5 },
      { nr: 3, name: "pm4_0", typ: "u16", einheit: "µg/m³", skala: 0.1, totband: 0.5 },
      { nr: 4, name: "pm10_0", typ: "u16", einheit: "µg/m³", skala: 0.1, totband: 0.5 },
      { nr: 5, name: "partikel_0_5", typ: "u16", einheit: "#/cm³", skala: 0.1, totband: 1.0 },
      { nr: 6, name: "partikel_2_5", typ: "u16", einheit: "#/cm³", skala: 0.1, totband: 1.0 },
      { nr: 7, name: "partikelgroesse", typ: "u16", einheit: "µm", skala: 0.001, totband: 0.01 },
    ],
  },
  {
    nr: 5,
    name: "druckwaechter.stand",
    art: "state",
    muster: "druckwaechter.stand",
    beschreibung: "Stand des Druckerwächters (Zustand, Bewertung, letzte Sicht, Einstellung)",
    rate: "normal",
    lebenszeichen_ms: 60000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "modus", typ: "WaechterModus" },
      { nr: 2, name: "zustand", typ: "WaechterZustand" },
      { nr: 3, name: "grund", typ: "string" },
      { nr: 4, name: "seit", typ: "zeit_ms" },
      { nr: 5, name: "datei", typ: "string" },
      { nr: 6, name: "stufe", typ: "WaechterStufe" },
      { nr: 7, name: "wert", typ: "u32", einheit: "1", skala: 0.0001 },
      { nr: 8, name: "glaettung", typ: "WaechterGlaettung" },
      { nr: 9, name: "verlauf", typ: "[u32]", einheit: "1", skala: 0.0001 },
      { nr: 10, name: "schonzeit", typ: "zeit_ms" },
      { nr: 11, name: "stumm", typ: "bool" },
      { nr: 12, name: "bilder_je_s", typ: "u16", einheit: "1/s", skala: 0.01 },
      { nr: 13, name: "erkenner_ms", typ: "u32", einheit: "ms" },
      { nr: 14, name: "erkenner", typ: "string" },
      { nr: 15, name: "takt_s", typ: "u16", einheit: "s" },
      { nr: 16, name: "sicht", typ: "WaechterSicht" },
      { nr: 17, name: "fragt", typ: "bool" },
      { nr: 18, name: "letzte_frage", typ: "WaechterMeinung" },
      { nr: 19, name: "offener_fall", typ: "string" },
      { nr: 20, name: "letzter_fall", typ: "string" },
      { nr: 21, name: "einstellung", typ: "WaechterEinstellung" },
    ],
  },
  {
    nr: 6,
    name: "erkenner.zustand",
    art: "state",
    muster: "erkenner.{id}.zustand",
    beschreibung: "Zustand eines Erkenner-Dienstes (Modell geladen, Bilder, Zeit je Bild)",
    rate: "normal",
    lebenszeichen_ms: 60000,
    lesen: ["nutzer"],
    schreiben: ["dienst"],
    felder: [
      { nr: 1, name: "bereit", typ: "bool" },
      { nr: 2, name: "modell", typ: "string" },
      { nr: 3, name: "threads", typ: "u8" },
      { nr: 4, name: "gestartet", typ: "zeit" },
      { nr: 5, name: "bilder", typ: "u32" },
      { nr: 6, name: "ms_letztes", typ: "u16", einheit: "ms" },
      { nr: 7, name: "ms_mittel", typ: "u16", einheit: "ms", totband: 3.0 },
      { nr: 8, name: "letzte_anfrage", typ: "zeit" },
      { nr: 9, name: "fehler", typ: "u32" },
    ],
  },
  {
    nr: 7,
    name: "task.lauf",
    art: "state",
    muster: "task.{id}.lauf",
    beschreibung: "Letzter Lauf einer Aufgabe (Status, Zeiten, Fehler, Fortschritt) und ihr naechster Termin",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "run", typ: "string" },
      { nr: 2, name: "status", typ: "LaufStatus" },
      { nr: 3, name: "start", typ: "zeit_ms" },
      { nr: 4, name: "ende", typ: "zeit_ms" },
      { nr: 5, name: "dauer_ms", typ: "u32", einheit: "ms" },
      { nr: 6, name: "fehler", typ: "string" },
      { nr: 7, name: "naechster", typ: "zeit_ms" },
      { nr: 8, name: "typische_dauer_ms", typ: "u32", einheit: "ms" },
      { nr: 9, name: "fortschritt", typ: "LaufFortschritt" },
    ],
  },
  {
    nr: 8,
    name: "geraet.stand",
    art: "state",
    muster: "geraet.{slug}.stand",
    beschreibung: "Ein Gerät der Geräte-Seite (Zigbee, Z-Wave, ESPHome): Stammdaten, Erreichbarkeit, Entitäten mit Zustand",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "name", typ: "string" },
      { nr: 2, name: "label", typ: "string" },
      { nr: 3, name: "bereich", typ: "string" },
      { nr: 4, name: "art", typ: "string" },
      { nr: 5, name: "hersteller", typ: "string" },
      { nr: 6, name: "modell", typ: "string" },
      { nr: 7, name: "integration", typ: "string" },
      { nr: 8, name: "integration_slug", typ: "string" },
      { nr: 9, name: "adresse", typ: "string" },
      { nr: 10, name: "online", typ: "bool" },
      { nr: 11, name: "zuletzt", typ: "zeit" },
      { nr: 12, name: "lqi", typ: "u8" },
      { nr: 13, name: "stilllegung_domain", typ: "string" },
      { nr: 14, name: "stilllegung_id", typ: "string" },
      { nr: 15, name: "versorgt", typ: "string" },
      { nr: 16, name: "aus_gesperrt", typ: "bool" },
      { nr: 17, name: "funkmelder", typ: "FunkRolle" },
      { nr: 18, name: "entitaeten", typ: "[GeraetEntitaet]" },
    ],
  },
  {
    nr: 9,
    name: "speicher.pool",
    art: "state",
    muster: "speicher.pool.{id}",
    beschreibung: "Ein ZFS-Pool (Kapazität, Zustand, Mitglieder, Scan, Ersatzplatten)",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "host", typ: "string" },
      { nr: 2, name: "host_id", typ: "string" },
      { nr: 3, name: "cluster", typ: "string" },
      { nr: 4, name: "name", typ: "string" },
      { nr: 5, name: "groesse", typ: "i64", einheit: "B" },
      { nr: 6, name: "belegt", typ: "i64", einheit: "B" },
      { nr: 7, name: "frei", typ: "i64", einheit: "B" },
      { nr: 8, name: "frag", typ: "string" },
      { nr: 9, name: "zustand", typ: "string" },
      { nr: 10, name: "nutzbar_groesse", typ: "i64", einheit: "B" },
      { nr: 11, name: "nutzbar_belegt", typ: "i64", einheit: "B" },
      { nr: 12, name: "nutzbar_frei", typ: "i64", einheit: "B" },
      { nr: 13, name: "overhead", typ: "i64", einheit: "B" },
      { nr: 14, name: "mitglieder", typ: "[SpeicherMitglied]" },
      { nr: 15, name: "scan", typ: "SpeicherScan" },
      { nr: 16, name: "status_text", typ: "string" },
      { nr: 17, name: "action_text", typ: "string" },
      { nr: 18, name: "ersatz", typ: "[SpeicherErsatz]" },
    ],
  },
  {
    nr: 10,
    name: "speicher.platte",
    art: "state",
    muster: "speicher.platte.{id}",
    beschreibung: "Eine Platte (Stammdaten, Zustand, Temperatur, SMART-Werte, Selbsttest) ohne das volle smartctl-JSON",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "host", typ: "string" },
      { nr: 2, name: "host_id", typ: "string" },
      { nr: 3, name: "cluster", typ: "string" },
      { nr: 4, name: "name", typ: "string" },
      { nr: 5, name: "groesse", typ: "i64", einheit: "B" },
      { nr: 6, name: "rotierend", typ: "bool" },
      { nr: 7, name: "anschluss", typ: "string" },
      { nr: 8, name: "modell", typ: "string" },
      { nr: 9, name: "zustand", typ: "string" },
      { nr: 10, name: "temp", typ: "string" },
      { nr: 11, name: "pool", typ: "string" },
      { nr: 12, name: "nvme", typ: "bool" },
      { nr: 13, name: "seriennummer", typ: "string" },
      { nr: 14, name: "firmware", typ: "string" },
      { nr: 15, name: "betriebsstunden", typ: "i64", einheit: "h" },
      { nr: 16, name: "geschrieben", typ: "i64", einheit: "B" },
      { nr: 17, name: "verschleiss", typ: "u8", einheit: "%" },
      { nr: 18, name: "umgelagert", typ: "i64" },
      { nr: 19, name: "schwebend", typ: "i64" },
      { nr: 20, name: "offline_unkorrigierbar", typ: "i64" },
      { nr: 21, name: "gemeldet_unkorrigierbar", typ: "i64" },
      { nr: 22, name: "crc_fehler", typ: "i64" },
      { nr: 23, name: "selbsttest_status", typ: "string" },
      { nr: 24, name: "selbsttest_ok", typ: "bool" },
      { nr: 25, name: "selbsttest_vor_h", typ: "i64", einheit: "h" },
      { nr: 26, name: "selbsttest_abbruch", typ: "string" },
      { nr: 27, name: "selbsttest_abbruch_vor_h", typ: "i64", einheit: "h" },
      { nr: 28, name: "selbsttest_laeuft", typ: "bool" },
      { nr: 29, name: "selbsttest_rest", typ: "u8", einheit: "%" },
      { nr: 30, name: "prefail", typ: "string" },
      { nr: 31, name: "partitionen", typ: "u16" },
      { nr: 32, name: "signaturen", typ: "[string]" },
      { nr: 33, name: "belegt", typ: "bool" },
    ],
  },
  {
    nr: 11,
    name: "speicher.dataset",
    art: "state",
    muster: "speicher.dataset.{id}",
    beschreibung: "Ein ZFS-Dataset (belegt, verfügbar, referenziert)",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "host", typ: "string" },
      { nr: 2, name: "cluster", typ: "string" },
      { nr: 3, name: "name", typ: "string" },
      { nr: 4, name: "belegt", typ: "i64", einheit: "B" },
      { nr: 5, name: "verfuegbar", typ: "i64", einheit: "B" },
      { nr: 6, name: "referenziert", typ: "i64", einheit: "B" },
      { nr: 7, name: "mountpoint", typ: "string" },
    ],
  },
  {
    nr: 12,
    name: "speicher.dateisystem",
    art: "state",
    muster: "speicher.dateisystem.{id}",
    beschreibung: "Ein Nicht-ZFS-Dateisystem (df-Sicht)",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "host", typ: "string" },
      { nr: 2, name: "cluster", typ: "string" },
      { nr: 3, name: "quelle", typ: "string" },
      { nr: 4, name: "mountpoint", typ: "string" },
      { nr: 5, name: "groesse", typ: "i64", einheit: "B" },
      { nr: 6, name: "belegt", typ: "i64", einheit: "B" },
      { nr: 7, name: "verfuegbar", typ: "i64", einheit: "B" },
    ],
  },
  {
    nr: 13,
    name: "speicher.lage",
    art: "state",
    muster: "speicher.lage",
    beschreibung: "Lage des Speicher-Collectors (letzter Lauf, Standard-Schwellen)",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "zuletzt", typ: "zeit_ms" },
      { nr: 2, name: "ok", typ: "bool" },
      { nr: 3, name: "warn_pct", typ: "u8", einheit: "%" },
      { nr: 4, name: "krit_pct", typ: "u8", einheit: "%" },
    ],
  },
  {
    nr: 14,
    name: "licht.bad",
    art: "state",
    muster: "licht.bad.{id}",
    beschreibung: "Eine Bewegungslicht-Regel (bathroom_light) mit Phasen, Auslösern und lernender Helligkeit",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "name", typ: "string" },
      { nr: 2, name: "aktiv", typ: "bool" },
      { nr: 3, name: "an", typ: "bool" },
      { nr: 4, name: "manuell", typ: "bool" },
      { nr: 5, name: "quelle", typ: "string" },
      { nr: 6, name: "lampe", typ: "string" },
      { nr: 7, name: "phase", typ: "string" },
      { nr: 8, name: "phase_label", typ: "string" },
      { nr: 9, name: "phasen", typ: "[BadPhase]" },
      { nr: 10, name: "sonnenaufgang", typ: "u32", einheit: "h", skala: 0.0001 },
      { nr: 11, name: "sonnenuntergang", typ: "u32", einheit: "h", skala: 0.0001 },
      { nr: 12, name: "naechste_phase", typ: "string" },
      { nr: 13, name: "naechste_phase_um", typ: "zeit" },
      { nr: 14, name: "abend_ende", typ: "u8", einheit: "h" },
      { nr: 15, name: "bewegung_topic", typ: "string" },
      { nr: 16, name: "tuer_topic", typ: "string" },
      { nr: 17, name: "dimmer_topic", typ: "string" },
      { nr: 18, name: "schalter_topics", typ: "[string]" },
      { nr: 19, name: "bewegung_frist", typ: "u32", einheit: "s" },
      { nr: 20, name: "tuer_verzug", typ: "u32", einheit: "s" },
      { nr: 21, name: "lernen", typ: "bool" },
      { nr: 22, name: "lernen_folgen", typ: "bool" },
      { nr: 23, name: "lern_anzahl", typ: "u32" },
      { nr: 24, name: "lern_proben", typ: "[LernPunkt]" },
      { nr: 25, name: "lern_kurve", typ: "[LernKurvenPunkt]" },
      { nr: 26, name: "lern_abschnitte", typ: "[LernAbschnitt]" },
      { nr: 27, name: "jetzt_helligkeit", typ: "u8" },
      { nr: 28, name: "jetzt_sicher", typ: "bool" },
      { nr: 29, name: "jetzt_vorlaeufig", typ: "u8" },
      { nr: 30, name: "wunsch", typ: "LernPunkt" },
    ],
  },
  {
    nr: 15,
    name: "licht.ambient",
    art: "state",
    muster: "licht.ambient.{id}",
    beschreibung: "Eine Ambient-Regel (ambient_group) mit Phasen, Voreinstellungen, Lampen und lernender Helligkeit",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "name", typ: "string" },
      { nr: 2, name: "aktiv", typ: "bool" },
      { nr: 3, name: "an", typ: "bool" },
      { nr: 4, name: "phase", typ: "string" },
      { nr: 5, name: "phase_label", typ: "string" },
      { nr: 6, name: "phasen", typ: "[AmbientPhase]" },
      { nr: 7, name: "szene", typ: "string" },
      { nr: 8, name: "sonnenaufgang", typ: "u32", einheit: "h", skala: 0.0001 },
      { nr: 9, name: "sonnenuntergang", typ: "u32", einheit: "h", skala: 0.0001 },
      { nr: 10, name: "naechste_phase", typ: "string" },
      { nr: 11, name: "naechste_phase_um", typ: "zeit" },
      { nr: 12, name: "ziele", typ: "[LichtZiel]" },
      { nr: 13, name: "uebergang", typ: "u16", einheit: "s" },
      { nr: 14, name: "uebergang_standard", typ: "u16", einheit: "s" },
      { nr: 15, name: "uebergang_max", typ: "u16", einheit: "s" },
      { nr: 16, name: "abend_ende", typ: "u8", einheit: "h" },
      { nr: 17, name: "lernen", typ: "bool" },
      { nr: 18, name: "lernen_folgen", typ: "bool" },
      { nr: 19, name: "lern_anzahl", typ: "u32" },
      { nr: 20, name: "lern_proben", typ: "[LernPunkt]" },
      { nr: 21, name: "lern_kurve", typ: "[LernKurvenPunkt]" },
      { nr: 22, name: "lern_abschnitte", typ: "[LernAbschnitt]" },
      { nr: 23, name: "jetzt_helligkeit", typ: "u8" },
      { nr: 24, name: "jetzt_sicher", typ: "bool" },
      { nr: 25, name: "jetzt_vorlaeufig", typ: "u8" },
      { nr: 26, name: "wunsch", typ: "LernPunkt" },
    ],
  },
  {
    nr: 16,
    name: "licht.szene",
    art: "state",
    muster: "licht.szene.{id}",
    beschreibung: "Eine Szene (scene) mit ihren Zielen",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "name", typ: "string" },
      { nr: 2, name: "aktiv", typ: "bool" },
      { nr: 3, name: "an", typ: "bool" },
      { nr: 4, name: "go_engine", typ: "bool" },
      { nr: 5, name: "ambient_zurueck", typ: "string" },
      { nr: 6, name: "ziel_anzahl", typ: "u16" },
      { nr: 7, name: "farben", typ: "[string]" },
      { nr: 8, name: "ziele", typ: "[SzenenZiel]" },
    ],
  },
  {
    nr: 17,
    name: "agent.stand",
    art: "state",
    muster: "agent.{id}.stand",
    beschreibung: "Ein Agent des Inventars (Edge-Komponente) mit Stammdaten, abgeleitetem Status, Takt-Messung und Deploy-Zustand",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "name", typ: "string" },
      { nr: 2, name: "art", typ: "string" },
      { nr: 3, name: "mqtt_topic", typ: "string" },
      { nr: 4, name: "lebenszeichen_s", typ: "u32", einheit: "s" },
      { nr: 5, name: "daten_s", typ: "u32", einheit: "s" },
      { nr: 6, name: "max_stille_s", typ: "u32", einheit: "s" },
      { nr: 7, name: "ssh_alias", typ: "string" },
      { nr: 8, name: "aktiv", typ: "bool" },
      { nr: 9, name: "notiz", typ: "string" },
      { nr: 10, name: "angelegt", typ: "string" },
      { nr: 11, name: "geaendert", typ: "string" },
      { nr: 12, name: "status", typ: "string" },
      { nr: 13, name: "zuletzt", typ: "zeit_ms" },
      { nr: 14, name: "abgemeldet_seit", typ: "zeit_ms" },
      { nr: 15, name: "deploy", typ: "AgentDeploy" },
      { nr: 16, name: "gemessen_s", typ: "u32", einheit: "s", skala: 0.01 },
      { nr: 17, name: "proben", typ: "u8" },
      { nr: 18, name: "abweichung", typ: "u32", einheit: "%", skala: 0.1 },
      { nr: 19, name: "soll_s", typ: "u32", einheit: "s" },
    ],
  },
  {
    nr: 18,
    name: "agent.kandidat",
    art: "state",
    muster: "agent.kandidat.{id}",
    beschreibung: "Ein beobachtetes Topic, das noch kein Agent ist (zum Übernehmen oder Verwerfen)",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "topic", typ: "string" },
      { nr: 2, name: "art", typ: "string" },
      { nr: 3, name: "name", typ: "string" },
      { nr: 4, name: "frames", typ: "u32" },
      { nr: 5, name: "erstmals", typ: "string" },
      { nr: 6, name: "zuletzt", typ: "string" },
    ],
  },
  {
    nr: 19,
    name: "steckdose.stand",
    art: "state",
    muster: "steckdose.{id}.stand",
    beschreibung: "Eine Steckdose oder ein Zähler (Z-Wave oder Zigbee) mit Schalter, Leistung, Zählerstand und Lebenszeichen",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "quelle", typ: "string" },
      { nr: 2, name: "name", typ: "string" },
      { nr: 3, name: "knoten", typ: "u16" },
      { nr: 4, name: "schalter", typ: "bool" },
      { nr: 5, name: "hat_schalter", typ: "bool" },
      { nr: 6, name: "watt", typ: "i32", einheit: "W", skala: 0.01 },
      { nr: 7, name: "kwh", typ: "u32", einheit: "kWh", skala: 0.001 },
      { nr: 8, name: "volt", typ: "u32", einheit: "V", skala: 0.01 },
      { nr: 9, name: "ampere", typ: "u32", einheit: "A", skala: 0.001 },
      { nr: 10, name: "online", typ: "bool" },
      { nr: 11, name: "kwh_gesamt", typ: "u32", einheit: "kWh", skala: 0.001 },
      { nr: 12, name: "zuletzt", typ: "zeit_ms" },
      { nr: 13, name: "gemessen", typ: "zeit_ms" },
    ],
  },
  {
    nr: 20,
    name: "growbox.stand",
    art: "state",
    muster: "growbox.{id}.stand",
    beschreibung: "Ein AC-Infinity-Controller mit Messwerten, Ports und aktiver Pflanzphase",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "name", typ: "string" },
      { nr: 2, name: "mac", typ: "string" },
      { nr: 3, name: "online", typ: "bool" },
      { nr: 4, name: "temperatur", typ: "i16", einheit: "°C", skala: 0.1 },
      { nr: 5, name: "feuchte", typ: "u16", einheit: "%", skala: 0.1 },
      { nr: 6, name: "vpd", typ: "u16", einheit: "kPa", skala: 0.01 },
      { nr: 7, name: "ports", typ: "[GrowboxPort]" },
      { nr: 8, name: "phase", typ: "string" },
      { nr: 9, name: "position", typ: "u16" },
    ],
  },
  {
    nr: 21,
    name: "growbox.lage",
    art: "state",
    muster: "growbox.lage",
    beschreibung: "Zustand des AC-Infinity-Zugangs (eingerichtet, angemeldet, Phasen, letzter Abruf)",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "eingerichtet", typ: "bool" },
      { nr: 2, name: "angemeldet", typ: "bool" },
      { nr: 3, name: "phasen", typ: "[string]" },
      { nr: 4, name: "letzter_abruf", typ: "zeit_ms" },
      { nr: 5, name: "email", typ: "string" },
    ],
  },
  {
    nr: 22,
    name: "sensor.stand",
    art: "state",
    muster: "sensor.{id}.stand",
    beschreibung: "Ein Sensor (Klima, CO2-Monitor, LiPo-Wächter oder Wassermelder) mit seinen letzten Werten",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "quelle", typ: "string" },
      { nr: 2, name: "name", typ: "string" },
      { nr: 3, name: "temperatur", typ: "i32", einheit: "°C", skala: 0.01 },
      { nr: 4, name: "feuchte", typ: "u32", einheit: "%", skala: 0.01 },
      { nr: 5, name: "druck", typ: "u32", einheit: "hPa", skala: 0.01 },
      { nr: 6, name: "batterie", typ: "u16", einheit: "%", skala: 0.01 },
      { nr: 7, name: "funk", typ: "u16" },
      { nr: 8, name: "bewegung", typ: "bool" },
      { nr: 9, name: "kontakt", typ: "bool" },
      { nr: 10, name: "kontakt_seit", typ: "zeit_ms" },
      { nr: 11, name: "wasser", typ: "bool" },
      { nr: 12, name: "status", typ: "string" },
      { nr: 13, name: "status_zeit", typ: "zeit_ms" },
      { nr: 14, name: "werte", typ: "[SensorWert]" },
      { nr: 15, name: "zuletzt", typ: "zeit_ms" },
    ],
  },
  {
    nr: 23,
    name: "klima.bewertung",
    art: "state",
    muster: "klima.{id}.bewertung",
    beschreibung: "Ein Sensor der Klima-Übersicht mit Messwerten, Zustand und Grund",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "domain", typ: "string" },
      { nr: 2, name: "sensor", typ: "string" },
      { nr: 3, name: "zeit", typ: "string" },
      { nr: 4, name: "werte", typ: "[SensorWert]" },
      { nr: 5, name: "batterie", typ: "u16", einheit: "%", skala: 0.01 },
      { nr: 6, name: "status", typ: "string" },
      { nr: 7, name: "grund", typ: "string" },
      { nr: 8, name: "aus", typ: "bool" },
      { nr: 9, name: "seit", typ: "string" },
      { nr: 10, name: "aussen", typ: "bool" },
      { nr: 11, name: "warn_nach", typ: "u32", einheit: "s" },
    ],
  },
  {
    nr: 24,
    name: "netz.cluster",
    art: "state",
    muster: "netz.{id}.cluster",
    beschreibung: "Ein Cluster mit seinen Rechnern und Netzgeräten und ob sie erreichbar sind",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "id", typ: "string" },
      { nr: 2, name: "name", typ: "string" },
      { nr: 3, name: "subnetz", typ: "string" },
      { nr: 4, name: "hosts", typ: "[NetzHost]" },
      { nr: 5, name: "geraete", typ: "[NetzGeraet]" },
      { nr: 6, name: "position", typ: "u16" },
    ],
  },
  {
    nr: 25,
    name: "openwrt.stand",
    art: "state",
    muster: "openwrt.{id}.stand",
    beschreibung: "WAN-Durchsatz, Tunnel und Tailscale eines OpenWrt-Routers",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "router", typ: "string" },
      { nr: 2, name: "host", typ: "string" },
      { nr: 3, name: "topic", typ: "string" },
      { nr: 4, name: "wan", typ: "string" },
      { nr: 5, name: "empfang", typ: "u32", einheit: "Mbit/s", skala: 0.01 },
      { nr: 6, name: "senden", typ: "u32", einheit: "Mbit/s", skala: 0.01 },
      { nr: 7, name: "tunnel", typ: "[OpenwrtTunnel]" },
      { nr: 8, name: "tailscale", typ: "OpenwrtTailscale" },
      { nr: 9, name: "zuletzt", typ: "zeit_ms" },
    ],
  },
  {
    nr: 26,
    name: "link.stand",
    art: "state",
    muster: "link.{id}.stand",
    beschreibung: "Zustand einer Netzwerkschnittstelle eines Rechners mit Ausfall und Flaps",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "entity", typ: "string" },
      { nr: 2, name: "host", typ: "string" },
      { nr: 3, name: "iface", typ: "string" },
      { nr: 4, name: "zustand", typ: "string" },
      { nr: 5, name: "gesundheit", typ: "string" },
      { nr: 6, name: "seit", typ: "string" },
      { nr: 7, name: "ausfall_seit", typ: "string" },
      { nr: 8, name: "ausfall", typ: "bool" },
      { nr: 9, name: "flaps", typ: "u32" },
      { nr: 10, name: "letzter_flap", typ: "string" },
      { nr: 11, name: "letztes_ereignis", typ: "string" },
    ],
  },
  {
    nr: 27,
    name: "topologie.knoten",
    art: "state",
    muster: "topologie.{id}.knoten",
    beschreibung: "Ein Knoten der Topologie (Router, Cluster, Rechner) mit Zustand, Verbindung und Prüfungen",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "id", typ: "string" },
      { nr: 2, name: "label", typ: "string" },
      { nr: 3, name: "art", typ: "string" },
      { nr: 4, name: "eltern", typ: "string" },
      { nr: 5, name: "cluster", typ: "string" },
      { nr: 6, name: "rolle", typ: "string" },
      { nr: 7, name: "ip", typ: "string" },
      { nr: 8, name: "os", typ: "string" },
      { nr: 9, name: "status", typ: "string" },
      { nr: 10, name: "link", typ: "TopoLink" },
      { nr: 11, name: "pruefungen", typ: "[TopoPruefung]" },
      { nr: 12, name: "position", typ: "u16" },
    ],
  },
  {
    nr: 28,
    name: "topologie.kanten",
    art: "state",
    muster: "topologie.kanten",
    beschreibung: "Die Kanten der Topologie und die Kanten der Uptime-Prüfungen",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "kanten", typ: "[TopoKante]" },
      { nr: 2, name: "pruefkanten", typ: "[TopoPruefkante]" },
    ],
  },
  {
    nr: 29,
    name: "netz.lage",
    art: "state",
    muster: "netz.lage",
    beschreibung: "Offene Rekey-Fenster und der Zustand der Integrationen (MQTT, zigbee2mqtt, Z-Wave, ESPHome)",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "rekey", typ: "[RekeyFenster]" },
      { nr: 2, name: "integrationen", typ: "[Integration]" },
    ],
  },
  {
    nr: 30,
    name: "wan.lage",
    art: "state",
    muster: "wan.lage",
    beschreibung: "Internet hinter forge (eigene Probe) und je Router, was sein Agent meldet",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "ok", typ: "bool" },
      { nr: 2, name: "status", typ: "string" },
      { nr: 3, name: "fehlversuche", typ: "u32" },
      { nr: 4, name: "geprueft", typ: "zeit_ms" },
      { nr: 5, name: "erfolg", typ: "zeit_ms" },
      { nr: 6, name: "fehlschlag", typ: "zeit_ms" },
      { nr: 7, name: "fehler", typ: "string" },
      { nr: 8, name: "ziel", typ: "string" },
      { nr: 9, name: "seit", typ: "zeit_ms" },
      { nr: 10, name: "ipv4", typ: "string" },
      { nr: 11, name: "ipv6", typ: "string" },
      { nr: 12, name: "empfang", typ: "u32", einheit: "Mbit/s", skala: 0.01 },
      { nr: 13, name: "senden", typ: "u32", einheit: "Mbit/s", skala: 0.01 },
      { nr: 14, name: "router", typ: "string" },
      { nr: 15, name: "routers", typ: "[WanRouter]" },
    ],
  },
  {
    nr: 31,
    name: "host.stand",
    art: "state",
    muster: "host.{host}.stand",
    beschreibung: "Ein Rechner mit Eintrag, online seit, Updates, SSH und Aufenthalt",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "id", typ: "string" },
      { nr: 2, name: "name", typ: "string" },
      { nr: 3, name: "ip", typ: "string" },
      { nr: 4, name: "mac", typ: "string" },
      { nr: 5, name: "rolle", typ: "string" },
      { nr: 6, name: "os", typ: "string" },
      { nr: 7, name: "schlafen_ok", typ: "bool" },
      { nr: 8, name: "temp_grenze", typ: "i32", einheit: "°C" },
      { nr: 9, name: "neustart_prio", typ: "i32" },
      { nr: 10, name: "neustart_tag", typ: "i32" },
      { nr: 11, name: "neustart_nach", typ: "[string]" },
      { nr: 12, name: "quelle", typ: "string" },
      { nr: 13, name: "online", typ: "bool" },
      { nr: 14, name: "seit", typ: "zeit_ms" },
      { nr: 15, name: "offline_24h", typ: "u32" },
      { nr: 16, name: "updates", typ: "HostUpdates" },
      { nr: 17, name: "ssh_ok", typ: "bool" },
      { nr: 18, name: "ssh_fehler", typ: "string" },
      { nr: 19, name: "ssh_adresse", typ: "string" },
      { nr: 20, name: "noch_nicht_in_betrieb", typ: "bool" },
      { nr: 21, name: "aufenthalt", typ: "HostAufenthalt" },
      { nr: 22, name: "cluster", typ: "string" },
      { nr: 23, name: "position", typ: "u16" },
    ],
  },
  {
    nr: 32,
    name: "host.lage",
    art: "state",
    muster: "host.lage",
    beschreibung: "Cluster-Reihenfolge, Anomalien gegen die Baseline und Rechner, die offline sein dürfen",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "cluster", typ: "[HostCluster]" },
      { nr: 2, name: "anomalien", typ: "[HostAnomalie]" },
      { nr: 3, name: "offline_ok", typ: "[string]" },
      { nr: 4, name: "verlauf_host", typ: "string" },
    ],
  },
  {
    nr: 33,
    name: "aktor.stand",
    art: "state",
    muster: "aktor.{id}.stand",
    beschreibung: "Ein Zigbee-Aktor mit Schaltzustand, Helligkeit, Farbe, Erreichbarkeit und Funk",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "name", typ: "string" },
      { nr: 2, name: "art", typ: "string" },
      { nr: 3, name: "zustand", typ: "string" },
      { nr: 4, name: "helligkeit", typ: "u16" },
      { nr: 5, name: "farbtemperatur", typ: "u16", einheit: "mired" },
      { nr: 6, name: "farbe", typ: "string" },
      { nr: 7, name: "zuletzt", typ: "zeit_ms" },
      { nr: 8, name: "erreichbar", typ: "bool" },
      { nr: 9, name: "batterie", typ: "u16", einheit: "%", skala: 0.1 },
      { nr: 10, name: "funk", typ: "u16" },
    ],
  },
  {
    nr: 34,
    name: "docker.stand",
    art: "state",
    muster: "docker.{id}.stand",
    beschreibung: "Ein Docker-Container mit Zustand, Image, Ports, CPU, Speicher und Grafik",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "rechner", typ: "string" },
      { nr: 2, name: "name", typ: "string" },
      { nr: 3, name: "image", typ: "string" },
      { nr: 4, name: "status", typ: "string" },
      { nr: 5, name: "zustand", typ: "string" },
      { nr: 6, name: "ports", typ: "string" },
      { nr: 7, name: "stack", typ: "string" },
      { nr: 8, name: "cpu", typ: "u32", einheit: "%", skala: 0.01, totband: 0.5 },
      { nr: 9, name: "speicher", typ: "u64", einheit: "B", totband: 4.194304e+06 },
      { nr: 10, name: "speicher_grenze", typ: "u64", einheit: "B" },
      { nr: 11, name: "speicher_anteil", typ: "u32", einheit: "%", skala: 0.01, totband: 0.5 },
      { nr: 12, name: "gpu", typ: "DockerGpu" },
      { nr: 13, name: "position", typ: "u16" },
    ],
  },
  {
    nr: 35,
    name: "docker.lage",
    art: "state",
    muster: "docker.lage",
    beschreibung: "Die Rechner mit Docker in ihrer Reihenfolge und ob sie antworten",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "rechner", typ: "[DockerRechner]" },
    ],
  },
  {
    nr: 36,
    name: "argus.teil",
    art: "state",
    muster: "argus.{teil}.stand",
    beschreibung: "Ein Teil des Argus-Stands (Feld der obersten Ebene) als JSON",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "json", typ: "string" },
    ],
  },
  {
    nr: 37,
    name: "argus.lauf",
    art: "state",
    muster: "argus.lauf.{lauf}.stand",
    beschreibung: "Ein Lauf des Argus-Trainings als JSON",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "json", typ: "string" },
    ],
  },
  {
    nr: 38,
    name: "argus.gross",
    art: "state",
    muster: "argus.{teil}.gross",
    beschreibung: "Ein großer Teil des Argus-Stands, nur für die Seiten, die ihn zeigen, als JSON",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "json", typ: "string" },
    ],
  },
  {
    nr: 39,
    name: "uptime.stand",
    art: "state",
    muster: "uptime.{ziel}.stand",
    beschreibung: "Ein überwachtes Ziel aus einem Blickwinkel (extern/intern) mit Zustand, Antwortzeit und Zertifikat",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "entity", typ: "string" },
      { nr: 2, name: "ziel_id", typ: "string" },
      { nr: 3, name: "bezeichnung", typ: "string" },
      { nr: 4, name: "blickwinkel", typ: "string" },
      { nr: 5, name: "url", typ: "string" },
      { nr: 6, name: "zustand", typ: "string" },
      { nr: 7, name: "ok", typ: "bool" },
      { nr: 8, name: "http_status", typ: "u16" },
      { nr: 9, name: "latenz", typ: "u32", einheit: "ms", totband: 20.0 },
      { nr: 10, name: "seit", typ: "zeit_ms" },
      { nr: 11, name: "fehlversuche", typ: "u32" },
      { nr: 12, name: "wackler", typ: "u32" },
      { nr: 13, name: "geprueft", typ: "zeit_ms" },
      { nr: 14, name: "fehler", typ: "string" },
      { nr: 15, name: "zertifikat_bis", typ: "zeit_ms" },
      { nr: 16, name: "zertifikat_tage", typ: "i32", einheit: "d" },
      { nr: 17, name: "position", typ: "u16" },
    ],
  },
  {
    nr: 40,
    name: "dns.lage",
    art: "state",
    muster: "dns.lage",
    beschreibung: "Entprellter Zustand des DNS-Resolvers (AdGuard) mit Server, Betriebsart und Antwortzeit",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "ok", typ: "bool" },
      { nr: 2, name: "zustand", typ: "string" },
      { nr: 3, name: "server", typ: "string" },
      { nr: 4, name: "modus", typ: "string" },
      { nr: 5, name: "seit", typ: "zeit_ms" },
      { nr: 6, name: "wackler", typ: "u32" },
      { nr: 7, name: "latenz", typ: "u32", einheit: "ms", totband: 20.0 },
    ],
  },
  {
    nr: 41,
    name: "bypass.lage",
    art: "state",
    muster: "bypass.lage",
    beschreibung: "Einstellung des Local-API-Bypass (an/aus, Auto-Aus in Minuten, seit wann)",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "an", typ: "bool" },
      { nr: 2, name: "auto_aus_min", typ: "u32", einheit: "min" },
      { nr: 3, name: "an_seit", typ: "zeit_ms" },
    ],
  },
  {
    nr: 42,
    name: "claude.lage",
    art: "state",
    muster: "claude.lage",
    beschreibung: "Stand von Claude ohne Chats und Repo-Stände (verbunden, Verbrauch, Modelle …), dazu die Reihenfolge der Chats und Repo-Stände, als JSON",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "json", typ: "string" },
    ],
  },
  {
    nr: 43,
    name: "claude.chat",
    art: "state",
    muster: "claude.chat.{chat}.stand",
    beschreibung: "Ein Claude-Chat in der freien Sicht ohne seine Commits, als JSON",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "json", typ: "string" },
    ],
  },
  {
    nr: 44,
    name: "claude.commits",
    art: "state",
    muster: "claude.chat.{chat}.commits",
    beschreibung: "Die Commits eines Claude-Chats (Trailer Homelab-Chat), jüngste zuerst, als JSON",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "json", typ: "string" },
    ],
  },
  {
    nr: 45,
    name: "claude.repo",
    art: "state",
    muster: "claude.repo.{repo}.stand",
    beschreibung: "Git-Stand eines Repos (offene Dateien, nicht gepushte Commits, Verlauf), als JSON",
    rate: "normal",
    lebenszeichen_ms: 300000,
    lesen: ["nutzer"],
    schreiben: ["backend"],
    felder: [
      { nr: 1, name: "json", typ: "string" },
    ],
  },
];

/** Felder der wiederverwendbaren Typen. */
export const TYPEN: Readonly<Record<string, readonly FeldInfo[]>> = {
  AgentDeploy: [
    { nr: 1, name: "status", typ: "string" },
    { nr: 2, name: "versuch", typ: "zeit_ms" },
    { nr: 3, name: "erfolg", typ: "zeit_ms" },
    { nr: 4, name: "fehler", typ: "string" },
    { nr: 5, name: "versuche", typ: "u16" },
    { nr: 6, name: "unerreichbar_seit", typ: "zeit_ms" },
  ],
  AmbientPhase: [
    { nr: 1, name: "schluessel", typ: "string" },
    { nr: 2, name: "label", typ: "string" },
    { nr: 3, name: "voreinstellungen", typ: "[LichtVoreinstellung]" },
    { nr: 4, name: "gewaehlt", typ: "u8" },
    { nr: 5, name: "helligkeit", typ: "u8" },
    { nr: 6, name: "standard_helligkeit", typ: "u8" },
    { nr: 7, name: "helligkeit_eigen", typ: "bool" },
    { nr: 8, name: "start", typ: "LichtGrenze" },
    { nr: 9, name: "start_stunde", typ: "u32", einheit: "h", skala: 0.0001 },
  ],
  BadPhase: [
    { nr: 1, name: "schluessel", typ: "string" },
    { nr: 2, name: "label", typ: "string" },
    { nr: 3, name: "helligkeit", typ: "u8" },
    { nr: 4, name: "standard_helligkeit", typ: "u8" },
    { nr: 5, name: "helligkeit_eigen", typ: "bool" },
    { nr: 6, name: "farbtemp", typ: "u16", einheit: "mired" },
    { nr: 7, name: "standard_farbtemp", typ: "u16", einheit: "mired" },
    { nr: 8, name: "farbtemp_eigen", typ: "bool" },
    { nr: 9, name: "uebergang", typ: "u16", einheit: "s" },
    { nr: 10, name: "start", typ: "LichtGrenze" },
    { nr: 11, name: "start_stunde", typ: "u32", einheit: "h", skala: 0.0001 },
  ],
  DockerGpu: [
    { nr: 1, name: "prozent", typ: "u16", einheit: "%", skala: 0.1 },
    { nr: 2, name: "speicher", typ: "u64", einheit: "B" },
    { nr: 3, name: "motoren", typ: "[DockerMotor]" },
  ],
  DockerMotor: [
    { nr: 1, name: "name", typ: "string" },
    { nr: 2, name: "prozent", typ: "u16", einheit: "%", skala: 0.1 },
  ],
  DockerRechner: [
    { nr: 1, name: "id", typ: "string" },
    { nr: 2, name: "name", typ: "string" },
    { nr: 3, name: "online", typ: "bool" },
  ],
  ErkennerStelle: [
    { nr: 1, name: "p", typ: "u16", einheit: "1", skala: 0.0001 },
    { nr: 2, name: "x1", typ: "u16", einheit: "1", skala: 0.0001 },
    { nr: 3, name: "y1", typ: "u16", einheit: "1", skala: 0.0001 },
    { nr: 4, name: "x2", typ: "u16", einheit: "1", skala: 0.0001 },
    { nr: 5, name: "y2", typ: "u16", einheit: "1", skala: 0.0001 },
  ],
  FunkDiagnose: [
    { nr: 1, name: "am", typ: "zeit" },
    { nr: 2, name: "erzeugt", typ: "u32", einheit: "1/s", skala: 0.01 },
    { nr: 3, name: "gesendet", typ: "u32", einheit: "1/s", skala: 0.01 },
    { nr: 4, name: "empfangen", typ: "u32", einheit: "1/s", skala: 0.01 },
    { nr: 5, name: "rueckrufe", typ: "u32", einheit: "1/s", skala: 0.01 },
    { nr: 6, name: "angenommen", typ: "u32", einheit: "1/s", skala: 0.01 },
    { nr: 7, name: "verwertet", typ: "u32", einheit: "1/s", skala: 0.01 },
    { nr: 8, name: "verworfen", typ: "u32", einheit: "1/s", skala: 0.01 },
    { nr: 9, name: "fehlend", typ: "u32", einheit: "1/s", skala: 0.01 },
    { nr: 10, name: "ueberzaehlig", typ: "u32", einheit: "1/s", skala: 0.01 },
    { nr: 11, name: "veraltet", typ: "u32", einheit: "1/s", skala: 0.01 },
    { nr: 12, name: "unsortiert", typ: "u32", einheit: "1/s", skala: 0.01 },
    { nr: 13, name: "belegung", typ: "u16", einheit: "%", skala: 0.1 },
    { nr: 14, name: "kanal", typ: "u8" },
    { nr: 15, name: "signal", typ: "i16", einheit: "dBm", skala: 0.1 },
    { nr: 16, name: "befund", typ: "FunkBefund" },
  ],
  FunkEinstellungen: [
    { nr: 1, name: "erkennung", typ: "FunkErkennung" },
    { nr: 2, name: "treffer_an", typ: "u8" },
    { nr: 3, name: "treffer_aus", typ: "u8" },
    { nr: 4, name: "haltezeit", typ: "u16", einheit: "s", skala: 0.1 },
    { nr: 5, name: "led", typ: "bool" },
  ],
  FunkRolle: [
    { nr: 1, name: "id", typ: "string" },
    { nr: 2, name: "aus", typ: "bool" },
    { nr: 3, name: "abschaltbar", typ: "bool" },
  ],
  GeraetEntitaet: [
    { nr: 1, name: "slug", typ: "string" },
    { nr: 2, name: "name", typ: "string" },
    { nr: 3, name: "art", typ: "string" },
    { nr: 4, name: "einheit", typ: "string" },
    { nr: 5, name: "zustand", typ: "string" },
    { nr: 6, name: "klasse", typ: "string" },
    { nr: 7, name: "ext_id", typ: "string" },
    { nr: 8, name: "aktualisiert", typ: "zeit" },
  ],
  GrowboxPort: [
    { nr: 1, name: "port", typ: "u8" },
    { nr: 2, name: "name", typ: "string" },
    { nr: 3, name: "art", typ: "string" },
    { nr: 4, name: "stufe", typ: "u8" },
    { nr: 5, name: "stufe_an", typ: "u8" },
    { nr: 6, name: "stufe_aus", typ: "u8" },
    { nr: 7, name: "an", typ: "bool" },
    { nr: 8, name: "modus", typ: "u8" },
    { nr: 9, name: "ueberschuss", typ: "i32" },
  ],
  GuteSchwelle: [
    { nr: 1, name: "schwelle", typ: "u16", einheit: "1", skala: 0.0001 },
    { nr: 2, name: "quelle", typ: "SchwellenQuelle" },
    { nr: 3, name: "am", typ: "zeit" },
  ],
  HostAnomalie: [
    { nr: 1, name: "host", typ: "string" },
    { nr: 2, name: "metrik", typ: "string" },
    { nr: 3, name: "aktuell", typ: "i32", skala: 0.01 },
    { nr: 4, name: "mittel", typ: "i32", skala: 0.01 },
    { nr: 5, name: "z", typ: "i32", skala: 0.01 },
  ],
  HostAufenthalt: [
    { nr: 1, name: "adresse", typ: "string" },
    { nr: 2, name: "eingetragen", typ: "string" },
    { nr: 3, name: "vom_agenten", typ: "bool" },
    { nr: 4, name: "ort", typ: "string" },
    { nr: 5, name: "netz", typ: "string" },
    { nr: 6, name: "gemeldet", typ: "zeit_ms" },
  ],
  HostCluster: [
    { nr: 1, name: "id", typ: "string" },
    { nr: 2, name: "name", typ: "string" },
  ],
  HostUpdates: [
    { nr: 1, name: "offen", typ: "u32" },
    { nr: 2, name: "zurueckgehalten", typ: "u32" },
    { nr: 3, name: "sicherheit", typ: "u32" },
    { nr: 4, name: "neustart", typ: "bool" },
    { nr: 5, name: "uu_aktiv", typ: "bool" },
    { nr: 6, name: "apt_zuletzt", typ: "i64", einheit: "s" },
    { nr: 7, name: "zeit", typ: "zeit_ms" },
  ],
  Integration: [
    { nr: 1, name: "id", typ: "string" },
    { nr: 2, name: "name", typ: "string" },
    { nr: 3, name: "status", typ: "string" },
    { nr: 4, name: "detail", typ: "string" },
    { nr: 5, name: "zuletzt", typ: "i64", einheit: "s" },
    { nr: 6, name: "geraete", typ: "u32" },
    { nr: 7, name: "geraete_online", typ: "u32" },
    { nr: 8, name: "gesamt", typ: "string" },
  ],
  KalibrierAuftrag: [
    { nr: 1, name: "seit", typ: "zeit" },
    { nr: 2, name: "bis", typ: "zeit" },
    { nr: 3, name: "laeuft", typ: "bool" },
    { nr: 4, name: "grund", typ: "string" },
  ],
  LaufFortschritt: [
    { nr: 1, name: "bytes", typ: "string" },
    { nr: 2, name: "pct", typ: "string" },
    { nr: 3, name: "speed", typ: "string" },
    { nr: 4, name: "eta", typ: "string" },
    { nr: 5, name: "aktualisiert", typ: "zeit_ms" },
  ],
  LernAbschnitt: [
    { nr: 1, name: "von_min", typ: "u16", einheit: "min" },
    { nr: 2, name: "bis_min", typ: "u16", einheit: "min" },
    { nr: 3, name: "helligkeit", typ: "u8" },
  ],
  LernKurvenPunkt: [
    { nr: 1, name: "minute", typ: "u16", einheit: "min" },
    { nr: 2, name: "helligkeit", typ: "u8" },
    { nr: 3, name: "sicher", typ: "bool" },
    { nr: 4, name: "gefuellt", typ: "bool" },
    { nr: 5, name: "vorlaeufig", typ: "u8" },
  ],
  LernPunkt: [
    { nr: 1, name: "minute", typ: "u16", einheit: "min" },
    { nr: 2, name: "helligkeit", typ: "u8" },
    { nr: 3, name: "bestaetigt", typ: "bool" },
  ],
  LichtGrenze: [
    { nr: 1, name: "sonne", typ: "string" },
    { nr: 2, name: "versatz_min", typ: "i16", einheit: "min" },
    { nr: 3, name: "uhrzeit", typ: "string" },
  ],
  LichtVoreinstellung: [
    { nr: 1, name: "name", typ: "string" },
    { nr: 2, name: "farben", typ: "[string]" },
  ],
  LichtZiel: [
    { nr: 1, name: "topic", typ: "string" },
    { nr: 2, name: "typ", typ: "string" },
  ],
  NetzGeraet: [
    { nr: 1, name: "name", typ: "string" },
    { nr: 2, name: "ip", typ: "string" },
    { nr: 3, name: "mac", typ: "string" },
    { nr: 4, name: "rolle", typ: "string" },
    { nr: 5, name: "verbunden_mit", typ: "string" },
    { nr: 6, name: "online", typ: "bool" },
  ],
  NetzHost: [
    { nr: 1, name: "id", typ: "string" },
    { nr: 2, name: "name", typ: "string" },
    { nr: 3, name: "ip", typ: "string" },
    { nr: 4, name: "mac", typ: "string" },
    { nr: 5, name: "rolle", typ: "string" },
    { nr: 6, name: "schlafen_ok", typ: "bool" },
    { nr: 7, name: "os", typ: "string" },
    { nr: 8, name: "temp_grenze", typ: "i32", einheit: "°C" },
    { nr: 9, name: "online", typ: "bool" },
    { nr: 10, name: "noch_nicht_in_betrieb", typ: "bool" },
  ],
  OpenwrtTailscale: [
    { nr: 1, name: "online", typ: "bool" },
    { nr: 2, name: "ipv4", typ: "string" },
    { nr: 3, name: "backend", typ: "string" },
    { nr: 4, name: "empfang", typ: "u32", einheit: "Mbit/s", skala: 0.01 },
    { nr: 5, name: "senden", typ: "u32", einheit: "Mbit/s", skala: 0.01 },
  ],
  OpenwrtTunnel: [
    { nr: 1, name: "name", typ: "string" },
    { nr: 2, name: "oben", typ: "bool" },
    { nr: 3, name: "laufzeit", typ: "i64", einheit: "s" },
    { nr: 4, name: "ipv4", typ: "string" },
    { nr: 5, name: "ipv6", typ: "string" },
  ],
  RekeyFenster: [
    { nr: 1, name: "host", typ: "string" },
    { nr: 2, name: "bis", typ: "zeit_ms" },
    { nr: 3, name: "geoeffnet", typ: "zeit_ms" },
    { nr: 4, name: "von", typ: "string" },
    { nr: 5, name: "grund", typ: "string" },
  ],
  SensorWert: [
    { nr: 1, name: "name", typ: "string" },
    { nr: 2, name: "wert", typ: "i64", skala: 0.0001 },
    { nr: 3, name: "zeit", typ: "zeit_ms" },
  ],
  SpeicherErsatz: [
    { nr: 1, name: "platte", typ: "string" },
    { nr: 2, name: "seriennummer", typ: "string" },
    { nr: 3, name: "modell", typ: "string" },
    { nr: 4, name: "groesse", typ: "i64", einheit: "B" },
    { nr: 5, name: "hat_daten", typ: "bool" },
    { nr: 6, name: "partitionen", typ: "u16" },
    { nr: 7, name: "signaturen", typ: "[string]" },
    { nr: 8, name: "mitglied", typ: "string" },
    { nr: 9, name: "mitglied_label", typ: "string" },
    { nr: 10, name: "smart_warnung", typ: "string" },
  ],
  SpeicherMitglied: [
    { nr: 1, name: "name", typ: "string" },
    { nr: 2, name: "pfad", typ: "string" },
    { nr: 3, name: "guid", typ: "string" },
    { nr: 4, name: "zustand", typ: "string" },
    { nr: 5, name: "lesefehler", typ: "string" },
    { nr: 6, name: "schreibfehler", typ: "string" },
    { nr: 7, name: "pruefsummenfehler", typ: "string" },
    { nr: 8, name: "gruppe", typ: "string" },
    { nr: 9, name: "resilver", typ: "bool" },
    { nr: 10, name: "notiz", typ: "string" },
    { nr: 11, name: "platte", typ: "string" },
    { nr: 12, name: "seriennummer", typ: "string" },
    { nr: 13, name: "label", typ: "string" },
  ],
  SpeicherScan: [
    { nr: 1, name: "art", typ: "string" },
    { nr: 2, name: "laeuft", typ: "bool" },
    { nr: 3, name: "prozent", typ: "u16", einheit: "%", skala: 0.01 },
    { nr: 4, name: "rest", typ: "string" },
    { nr: 5, name: "tempo", typ: "string" },
    { nr: 6, name: "menge", typ: "string" },
    { nr: 7, name: "text", typ: "string" },
  ],
  Strom: [
    { nr: 1, name: "throttled_now", typ: "u8", einheit: "bits" },
    { nr: 2, name: "throttled_seen", typ: "u8", einheit: "bits" },
    { nr: 3, name: "volt_core", typ: "u16", einheit: "V", skala: 0.001 },
    { nr: 4, name: "throttled_hex", typ: "string" },
  ],
  SzenenZiel: [
    { nr: 1, name: "topic", typ: "string" },
    { nr: 2, name: "typ", typ: "string" },
    { nr: 3, name: "payload", typ: "string" },
  ],
  TopoKante: [
    { nr: 1, name: "id", typ: "string" },
    { nr: 2, name: "quelle", typ: "string" },
    { nr: 3, name: "ziel", typ: "string" },
    { nr: 4, name: "art", typ: "string" },
    { nr: 5, name: "label", typ: "string" },
  ],
  TopoLink: [
    { nr: 1, name: "gesundheit", typ: "string" },
    { nr: 2, name: "iface", typ: "string" },
    { nr: 3, name: "flaps", typ: "u32" },
    { nr: 4, name: "letzter_flap", typ: "string" },
    { nr: 5, name: "ausfall", typ: "bool" },
  ],
  TopoPruefkante: [
    { nr: 1, name: "id", typ: "string" },
    { nr: 2, name: "quelle", typ: "string" },
    { nr: 3, name: "ziel", typ: "string" },
    { nr: 4, name: "blick", typ: "string" },
    { nr: 5, name: "gesundheit", typ: "string" },
    { nr: 6, name: "label", typ: "string" },
  ],
  TopoPruefung: [
    { nr: 1, name: "blick", typ: "string" },
    { nr: 2, name: "gesundheit", typ: "string" },
    { nr: 3, name: "http_status", typ: "u16" },
    { nr: 4, name: "latenz", typ: "u32", einheit: "ms" },
    { nr: 5, name: "url", typ: "string" },
    { nr: 6, name: "notiz", typ: "string" },
    { nr: 7, name: "zuletzt", typ: "string" },
  ],
  WaechterEinstellung: [
    { nr: 1, name: "modus", typ: "WaechterModus" },
    { nr: 2, name: "empfindlichkeit", typ: "u16", einheit: "1", skala: 0.001 },
    { nr: 3, name: "schwelle_niedrig", typ: "u16", einheit: "1", skala: 0.001 },
    { nr: 4, name: "schwelle_hoch", typ: "u16", einheit: "1", skala: 0.001 },
    { nr: 5, name: "steigerung", typ: "u16", einheit: "1", skala: 0.001 },
    { nr: 6, name: "schonzeit_min", typ: "i16", einheit: "min" },
    { nr: 7, name: "ohne_meinung", typ: "bool" },
    { nr: 8, name: "modell", typ: "string" },
  ],
  WaechterGlaettung: [
    { nr: 1, name: "ewm", typ: "u32", einheit: "1", skala: 0.0001 },
    { nr: 2, name: "kurz", typ: "u32", einheit: "1", skala: 0.0001 },
    { nr: 3, name: "lang", typ: "u32", einheit: "1", skala: 0.0001 },
    { nr: 4, name: "takte", typ: "u32" },
    { nr: 5, name: "takte_gesamt", typ: "u32" },
    { nr: 6, name: "p", typ: "u32", einheit: "1", skala: 0.0001 },
  ],
  WaechterMeinung: [
    { nr: 1, name: "urteil", typ: "string" },
    { nr: 2, name: "art", typ: "string" },
    { nr: 3, name: "begruendung", typ: "string" },
    { nr: 4, name: "modell", typ: "string" },
    { nr: 5, name: "ms", typ: "u32", einheit: "ms" },
    { nr: 6, name: "fehler", typ: "string" },
    { nr: 7, name: "ausschnitt", typ: "bool" },
  ],
  WaechterSicht: [
    { nr: 1, name: "zeit", typ: "zeit_ms" },
    { nr: 2, name: "stellen", typ: "[ErkennerStelle]" },
    { nr: 3, name: "breite", typ: "u16", einheit: "px" },
    { nr: 4, name: "hoehe", typ: "u16", einheit: "px" },
    { nr: 5, name: "p", typ: "u32", einheit: "1", skala: 0.0001 },
    { nr: 6, name: "bewertet", typ: "bool" },
    { nr: 7, name: "hinweis", typ: "string" },
  ],
  WanRouter: [
    { nr: 1, name: "host", typ: "string" },
    { nr: 2, name: "name", typ: "string" },
    { nr: 3, name: "cluster", typ: "string" },
    { nr: 4, name: "cluster_name", typ: "string" },
    { nr: 5, name: "quelle", typ: "string" },
    { nr: 6, name: "status", typ: "string" },
    { nr: 7, name: "seit", typ: "zeit_ms" },
    { nr: 8, name: "zuletzt", typ: "zeit_ms" },
    { nr: 9, name: "stumm", typ: "bool" },
    { nr: 10, name: "wan_oben", typ: "bool" },
    { nr: 11, name: "ipv4", typ: "string" },
    { nr: 12, name: "ipv6", typ: "string" },
    { nr: 13, name: "empfang", typ: "u32", einheit: "Mbit/s", skala: 0.01 },
    { nr: 14, name: "senden", typ: "u32", einheit: "Mbit/s", skala: 0.01 },
  ],
  Wlan: [
    { nr: 1, name: "clients", typ: "u16" },
    { nr: 2, name: "bands", typ: "[WlanBandZahl]" },
  ],
  WlanBandZahl: [
    { nr: 1, name: "band", typ: "WlanBand" },
    { nr: 2, name: "clients", typ: "u16" },
  ],
};

/** Höchstraten je Klasse in Millisekunden (0 = jede Änderung). */
export const RATEN_MS: Readonly<Record<string, number>> = {
  live: 0,
  niedrig: 30000,
  normal: 1000,
};

/** Feldname → Feldnummer, je Topic (Name) und Typ. */
export const FELD_NR = {
  "host.stats": {
    cpu: 1,
    cpu_temp: 2,
    mem_pct: 3,
    uptime_s: 4,
    power: 5,
    wifi: 6,
    adressen: 7,
  },
  "funkmelder.bewegung": {
    lage: 1,
    seit: 2,
    wert: 3,
    schwelle: 4,
    gemessen_am: 5,
    kategorie: 6,
    einstellungen: 7,
    diagnose: 8,
    rohdaten: 9,
    gute_schwelle: 10,
    auftrag: 11,
    aus: 12,
    abschaltbar: 13,
  },
  meldung: {
    art: 1,
    domain: 2,
    type: 3,
    severity: 4,
    entity: 5,
    summary: 6,
    zeit: 7,
  },
  "feinstaub.luft": {
    pm1_0: 1,
    pm2_5: 2,
    pm4_0: 3,
    pm10_0: 4,
    partikel_0_5: 5,
    partikel_2_5: 6,
    partikelgroesse: 7,
  },
  "druckwaechter.stand": {
    modus: 1,
    zustand: 2,
    grund: 3,
    seit: 4,
    datei: 5,
    stufe: 6,
    wert: 7,
    glaettung: 8,
    verlauf: 9,
    schonzeit: 10,
    stumm: 11,
    bilder_je_s: 12,
    erkenner_ms: 13,
    erkenner: 14,
    takt_s: 15,
    sicht: 16,
    fragt: 17,
    letzte_frage: 18,
    offener_fall: 19,
    letzter_fall: 20,
    einstellung: 21,
  },
  "erkenner.zustand": {
    bereit: 1,
    modell: 2,
    threads: 3,
    gestartet: 4,
    bilder: 5,
    ms_letztes: 6,
    ms_mittel: 7,
    letzte_anfrage: 8,
    fehler: 9,
  },
  "task.lauf": {
    run: 1,
    status: 2,
    start: 3,
    ende: 4,
    dauer_ms: 5,
    fehler: 6,
    naechster: 7,
    typische_dauer_ms: 8,
    fortschritt: 9,
  },
  "geraet.stand": {
    name: 1,
    label: 2,
    bereich: 3,
    art: 4,
    hersteller: 5,
    modell: 6,
    integration: 7,
    integration_slug: 8,
    adresse: 9,
    online: 10,
    zuletzt: 11,
    lqi: 12,
    stilllegung_domain: 13,
    stilllegung_id: 14,
    versorgt: 15,
    aus_gesperrt: 16,
    funkmelder: 17,
    entitaeten: 18,
  },
  "speicher.pool": {
    host: 1,
    host_id: 2,
    cluster: 3,
    name: 4,
    groesse: 5,
    belegt: 6,
    frei: 7,
    frag: 8,
    zustand: 9,
    nutzbar_groesse: 10,
    nutzbar_belegt: 11,
    nutzbar_frei: 12,
    overhead: 13,
    mitglieder: 14,
    scan: 15,
    status_text: 16,
    action_text: 17,
    ersatz: 18,
  },
  "speicher.platte": {
    host: 1,
    host_id: 2,
    cluster: 3,
    name: 4,
    groesse: 5,
    rotierend: 6,
    anschluss: 7,
    modell: 8,
    zustand: 9,
    temp: 10,
    pool: 11,
    nvme: 12,
    seriennummer: 13,
    firmware: 14,
    betriebsstunden: 15,
    geschrieben: 16,
    verschleiss: 17,
    umgelagert: 18,
    schwebend: 19,
    offline_unkorrigierbar: 20,
    gemeldet_unkorrigierbar: 21,
    crc_fehler: 22,
    selbsttest_status: 23,
    selbsttest_ok: 24,
    selbsttest_vor_h: 25,
    selbsttest_abbruch: 26,
    selbsttest_abbruch_vor_h: 27,
    selbsttest_laeuft: 28,
    selbsttest_rest: 29,
    prefail: 30,
    partitionen: 31,
    signaturen: 32,
    belegt: 33,
  },
  "speicher.dataset": {
    host: 1,
    cluster: 2,
    name: 3,
    belegt: 4,
    verfuegbar: 5,
    referenziert: 6,
    mountpoint: 7,
  },
  "speicher.dateisystem": {
    host: 1,
    cluster: 2,
    quelle: 3,
    mountpoint: 4,
    groesse: 5,
    belegt: 6,
    verfuegbar: 7,
  },
  "speicher.lage": {
    zuletzt: 1,
    ok: 2,
    warn_pct: 3,
    krit_pct: 4,
  },
  "licht.bad": {
    name: 1,
    aktiv: 2,
    an: 3,
    manuell: 4,
    quelle: 5,
    lampe: 6,
    phase: 7,
    phase_label: 8,
    phasen: 9,
    sonnenaufgang: 10,
    sonnenuntergang: 11,
    naechste_phase: 12,
    naechste_phase_um: 13,
    abend_ende: 14,
    bewegung_topic: 15,
    tuer_topic: 16,
    dimmer_topic: 17,
    schalter_topics: 18,
    bewegung_frist: 19,
    tuer_verzug: 20,
    lernen: 21,
    lernen_folgen: 22,
    lern_anzahl: 23,
    lern_proben: 24,
    lern_kurve: 25,
    lern_abschnitte: 26,
    jetzt_helligkeit: 27,
    jetzt_sicher: 28,
    jetzt_vorlaeufig: 29,
    wunsch: 30,
  },
  "licht.ambient": {
    name: 1,
    aktiv: 2,
    an: 3,
    phase: 4,
    phase_label: 5,
    phasen: 6,
    szene: 7,
    sonnenaufgang: 8,
    sonnenuntergang: 9,
    naechste_phase: 10,
    naechste_phase_um: 11,
    ziele: 12,
    uebergang: 13,
    uebergang_standard: 14,
    uebergang_max: 15,
    abend_ende: 16,
    lernen: 17,
    lernen_folgen: 18,
    lern_anzahl: 19,
    lern_proben: 20,
    lern_kurve: 21,
    lern_abschnitte: 22,
    jetzt_helligkeit: 23,
    jetzt_sicher: 24,
    jetzt_vorlaeufig: 25,
    wunsch: 26,
  },
  "licht.szene": {
    name: 1,
    aktiv: 2,
    an: 3,
    go_engine: 4,
    ambient_zurueck: 5,
    ziel_anzahl: 6,
    farben: 7,
    ziele: 8,
  },
  "agent.stand": {
    name: 1,
    art: 2,
    mqtt_topic: 3,
    lebenszeichen_s: 4,
    daten_s: 5,
    max_stille_s: 6,
    ssh_alias: 7,
    aktiv: 8,
    notiz: 9,
    angelegt: 10,
    geaendert: 11,
    status: 12,
    zuletzt: 13,
    abgemeldet_seit: 14,
    deploy: 15,
    gemessen_s: 16,
    proben: 17,
    abweichung: 18,
    soll_s: 19,
  },
  "agent.kandidat": {
    topic: 1,
    art: 2,
    name: 3,
    frames: 4,
    erstmals: 5,
    zuletzt: 6,
  },
  "steckdose.stand": {
    quelle: 1,
    name: 2,
    knoten: 3,
    schalter: 4,
    hat_schalter: 5,
    watt: 6,
    kwh: 7,
    volt: 8,
    ampere: 9,
    online: 10,
    kwh_gesamt: 11,
    zuletzt: 12,
    gemessen: 13,
  },
  "growbox.stand": {
    name: 1,
    mac: 2,
    online: 3,
    temperatur: 4,
    feuchte: 5,
    vpd: 6,
    ports: 7,
    phase: 8,
    position: 9,
  },
  "growbox.lage": {
    eingerichtet: 1,
    angemeldet: 2,
    phasen: 3,
    letzter_abruf: 4,
    email: 5,
  },
  "sensor.stand": {
    quelle: 1,
    name: 2,
    temperatur: 3,
    feuchte: 4,
    druck: 5,
    batterie: 6,
    funk: 7,
    bewegung: 8,
    kontakt: 9,
    kontakt_seit: 10,
    wasser: 11,
    status: 12,
    status_zeit: 13,
    werte: 14,
    zuletzt: 15,
  },
  "klima.bewertung": {
    domain: 1,
    sensor: 2,
    zeit: 3,
    werte: 4,
    batterie: 5,
    status: 6,
    grund: 7,
    aus: 8,
    seit: 9,
    aussen: 10,
    warn_nach: 11,
  },
  "netz.cluster": {
    id: 1,
    name: 2,
    subnetz: 3,
    hosts: 4,
    geraete: 5,
    position: 6,
  },
  "openwrt.stand": {
    router: 1,
    host: 2,
    topic: 3,
    wan: 4,
    empfang: 5,
    senden: 6,
    tunnel: 7,
    tailscale: 8,
    zuletzt: 9,
  },
  "link.stand": {
    entity: 1,
    host: 2,
    iface: 3,
    zustand: 4,
    gesundheit: 5,
    seit: 6,
    ausfall_seit: 7,
    ausfall: 8,
    flaps: 9,
    letzter_flap: 10,
    letztes_ereignis: 11,
  },
  "topologie.knoten": {
    id: 1,
    label: 2,
    art: 3,
    eltern: 4,
    cluster: 5,
    rolle: 6,
    ip: 7,
    os: 8,
    status: 9,
    link: 10,
    pruefungen: 11,
    position: 12,
  },
  "topologie.kanten": {
    kanten: 1,
    pruefkanten: 2,
  },
  "netz.lage": {
    rekey: 1,
    integrationen: 2,
  },
  "wan.lage": {
    ok: 1,
    status: 2,
    fehlversuche: 3,
    geprueft: 4,
    erfolg: 5,
    fehlschlag: 6,
    fehler: 7,
    ziel: 8,
    seit: 9,
    ipv4: 10,
    ipv6: 11,
    empfang: 12,
    senden: 13,
    router: 14,
    routers: 15,
  },
  "host.stand": {
    id: 1,
    name: 2,
    ip: 3,
    mac: 4,
    rolle: 5,
    os: 6,
    schlafen_ok: 7,
    temp_grenze: 8,
    neustart_prio: 9,
    neustart_tag: 10,
    neustart_nach: 11,
    quelle: 12,
    online: 13,
    seit: 14,
    offline_24h: 15,
    updates: 16,
    ssh_ok: 17,
    ssh_fehler: 18,
    ssh_adresse: 19,
    noch_nicht_in_betrieb: 20,
    aufenthalt: 21,
    cluster: 22,
    position: 23,
  },
  "host.lage": {
    cluster: 1,
    anomalien: 2,
    offline_ok: 3,
    verlauf_host: 4,
  },
  "aktor.stand": {
    name: 1,
    art: 2,
    zustand: 3,
    helligkeit: 4,
    farbtemperatur: 5,
    farbe: 6,
    zuletzt: 7,
    erreichbar: 8,
    batterie: 9,
    funk: 10,
  },
  "docker.stand": {
    rechner: 1,
    name: 2,
    image: 3,
    status: 4,
    zustand: 5,
    ports: 6,
    stack: 7,
    cpu: 8,
    speicher: 9,
    speicher_grenze: 10,
    speicher_anteil: 11,
    gpu: 12,
    position: 13,
  },
  "docker.lage": {
    rechner: 1,
  },
  "argus.teil": {
    json: 1,
  },
  "argus.lauf": {
    json: 1,
  },
  "argus.gross": {
    json: 1,
  },
  "uptime.stand": {
    entity: 1,
    ziel_id: 2,
    bezeichnung: 3,
    blickwinkel: 4,
    url: 5,
    zustand: 6,
    ok: 7,
    http_status: 8,
    latenz: 9,
    seit: 10,
    fehlversuche: 11,
    wackler: 12,
    geprueft: 13,
    fehler: 14,
    zertifikat_bis: 15,
    zertifikat_tage: 16,
    position: 17,
  },
  "dns.lage": {
    ok: 1,
    zustand: 2,
    server: 3,
    modus: 4,
    seit: 5,
    wackler: 6,
    latenz: 7,
  },
  "bypass.lage": {
    an: 1,
    auto_aus_min: 2,
    an_seit: 3,
  },
  "claude.lage": {
    json: 1,
  },
  "claude.chat": {
    json: 1,
  },
  "claude.commits": {
    json: 1,
  },
  "claude.repo": {
    json: 1,
  },
  AgentDeploy: {
    status: 1,
    versuch: 2,
    erfolg: 3,
    fehler: 4,
    versuche: 5,
    unerreichbar_seit: 6,
  },
  AmbientPhase: {
    schluessel: 1,
    label: 2,
    voreinstellungen: 3,
    gewaehlt: 4,
    helligkeit: 5,
    standard_helligkeit: 6,
    helligkeit_eigen: 7,
    start: 8,
    start_stunde: 9,
  },
  BadPhase: {
    schluessel: 1,
    label: 2,
    helligkeit: 3,
    standard_helligkeit: 4,
    helligkeit_eigen: 5,
    farbtemp: 6,
    standard_farbtemp: 7,
    farbtemp_eigen: 8,
    uebergang: 9,
    start: 10,
    start_stunde: 11,
  },
  DockerGpu: {
    prozent: 1,
    speicher: 2,
    motoren: 3,
  },
  DockerMotor: {
    name: 1,
    prozent: 2,
  },
  DockerRechner: {
    id: 1,
    name: 2,
    online: 3,
  },
  ErkennerStelle: {
    p: 1,
    x1: 2,
    y1: 3,
    x2: 4,
    y2: 5,
  },
  FunkDiagnose: {
    am: 1,
    erzeugt: 2,
    gesendet: 3,
    empfangen: 4,
    rueckrufe: 5,
    angenommen: 6,
    verwertet: 7,
    verworfen: 8,
    fehlend: 9,
    ueberzaehlig: 10,
    veraltet: 11,
    unsortiert: 12,
    belegung: 13,
    kanal: 14,
    signal: 15,
    befund: 16,
  },
  FunkEinstellungen: {
    erkennung: 1,
    treffer_an: 2,
    treffer_aus: 3,
    haltezeit: 4,
    led: 5,
  },
  FunkRolle: {
    id: 1,
    aus: 2,
    abschaltbar: 3,
  },
  GeraetEntitaet: {
    slug: 1,
    name: 2,
    art: 3,
    einheit: 4,
    zustand: 5,
    klasse: 6,
    ext_id: 7,
    aktualisiert: 8,
  },
  GrowboxPort: {
    port: 1,
    name: 2,
    art: 3,
    stufe: 4,
    stufe_an: 5,
    stufe_aus: 6,
    an: 7,
    modus: 8,
    ueberschuss: 9,
  },
  GuteSchwelle: {
    schwelle: 1,
    quelle: 2,
    am: 3,
  },
  HostAnomalie: {
    host: 1,
    metrik: 2,
    aktuell: 3,
    mittel: 4,
    z: 5,
  },
  HostAufenthalt: {
    adresse: 1,
    eingetragen: 2,
    vom_agenten: 3,
    ort: 4,
    netz: 5,
    gemeldet: 6,
  },
  HostCluster: {
    id: 1,
    name: 2,
  },
  HostUpdates: {
    offen: 1,
    zurueckgehalten: 2,
    sicherheit: 3,
    neustart: 4,
    uu_aktiv: 5,
    apt_zuletzt: 6,
    zeit: 7,
  },
  Integration: {
    id: 1,
    name: 2,
    status: 3,
    detail: 4,
    zuletzt: 5,
    geraete: 6,
    geraete_online: 7,
    gesamt: 8,
  },
  KalibrierAuftrag: {
    seit: 1,
    bis: 2,
    laeuft: 3,
    grund: 4,
  },
  LaufFortschritt: {
    bytes: 1,
    pct: 2,
    speed: 3,
    eta: 4,
    aktualisiert: 5,
  },
  LernAbschnitt: {
    von_min: 1,
    bis_min: 2,
    helligkeit: 3,
  },
  LernKurvenPunkt: {
    minute: 1,
    helligkeit: 2,
    sicher: 3,
    gefuellt: 4,
    vorlaeufig: 5,
  },
  LernPunkt: {
    minute: 1,
    helligkeit: 2,
    bestaetigt: 3,
  },
  LichtGrenze: {
    sonne: 1,
    versatz_min: 2,
    uhrzeit: 3,
  },
  LichtVoreinstellung: {
    name: 1,
    farben: 2,
  },
  LichtZiel: {
    topic: 1,
    typ: 2,
  },
  NetzGeraet: {
    name: 1,
    ip: 2,
    mac: 3,
    rolle: 4,
    verbunden_mit: 5,
    online: 6,
  },
  NetzHost: {
    id: 1,
    name: 2,
    ip: 3,
    mac: 4,
    rolle: 5,
    schlafen_ok: 6,
    os: 7,
    temp_grenze: 8,
    online: 9,
    noch_nicht_in_betrieb: 10,
  },
  OpenwrtTailscale: {
    online: 1,
    ipv4: 2,
    backend: 3,
    empfang: 4,
    senden: 5,
  },
  OpenwrtTunnel: {
    name: 1,
    oben: 2,
    laufzeit: 3,
    ipv4: 4,
    ipv6: 5,
  },
  RekeyFenster: {
    host: 1,
    bis: 2,
    geoeffnet: 3,
    von: 4,
    grund: 5,
  },
  SensorWert: {
    name: 1,
    wert: 2,
    zeit: 3,
  },
  SpeicherErsatz: {
    platte: 1,
    seriennummer: 2,
    modell: 3,
    groesse: 4,
    hat_daten: 5,
    partitionen: 6,
    signaturen: 7,
    mitglied: 8,
    mitglied_label: 9,
    smart_warnung: 10,
  },
  SpeicherMitglied: {
    name: 1,
    pfad: 2,
    guid: 3,
    zustand: 4,
    lesefehler: 5,
    schreibfehler: 6,
    pruefsummenfehler: 7,
    gruppe: 8,
    resilver: 9,
    notiz: 10,
    platte: 11,
    seriennummer: 12,
    label: 13,
  },
  SpeicherScan: {
    art: 1,
    laeuft: 2,
    prozent: 3,
    rest: 4,
    tempo: 5,
    menge: 6,
    text: 7,
  },
  Strom: {
    throttled_now: 1,
    throttled_seen: 2,
    volt_core: 3,
    throttled_hex: 4,
  },
  SzenenZiel: {
    topic: 1,
    typ: 2,
    payload: 3,
  },
  TopoKante: {
    id: 1,
    quelle: 2,
    ziel: 3,
    art: 4,
    label: 5,
  },
  TopoLink: {
    gesundheit: 1,
    iface: 2,
    flaps: 3,
    letzter_flap: 4,
    ausfall: 5,
  },
  TopoPruefkante: {
    id: 1,
    quelle: 2,
    ziel: 3,
    blick: 4,
    gesundheit: 5,
    label: 6,
  },
  TopoPruefung: {
    blick: 1,
    gesundheit: 2,
    http_status: 3,
    latenz: 4,
    url: 5,
    notiz: 6,
    zuletzt: 7,
  },
  WaechterEinstellung: {
    modus: 1,
    empfindlichkeit: 2,
    schwelle_niedrig: 3,
    schwelle_hoch: 4,
    steigerung: 5,
    schonzeit_min: 6,
    ohne_meinung: 7,
    modell: 8,
  },
  WaechterGlaettung: {
    ewm: 1,
    kurz: 2,
    lang: 3,
    takte: 4,
    takte_gesamt: 5,
    p: 6,
  },
  WaechterMeinung: {
    urteil: 1,
    art: 2,
    begruendung: 3,
    modell: 4,
    ms: 5,
    fehler: 6,
    ausschnitt: 7,
  },
  WaechterSicht: {
    zeit: 1,
    stellen: 2,
    breite: 3,
    hoehe: 4,
    p: 5,
    bewertet: 6,
    hinweis: 7,
  },
  WanRouter: {
    host: 1,
    name: 2,
    cluster: 3,
    cluster_name: 4,
    quelle: 5,
    status: 6,
    seit: 7,
    zuletzt: 8,
    stumm: 9,
    wan_oben: 10,
    ipv4: 11,
    ipv6: 12,
    empfang: 13,
    senden: 14,
  },
  Wlan: {
    clients: 1,
    bands: 2,
  },
  WlanBandZahl: {
    band: 1,
    clients: 2,
  },
} as const;

/** Feldnummer → Feldname, je Topic (Name) und Typ. */
export const FELD_NAME = {
  "host.stats": {
    1: "cpu",
    2: "cpu_temp",
    3: "mem_pct",
    4: "uptime_s",
    5: "power",
    6: "wifi",
    7: "adressen",
  },
  "funkmelder.bewegung": {
    1: "lage",
    2: "seit",
    3: "wert",
    4: "schwelle",
    5: "gemessen_am",
    6: "kategorie",
    7: "einstellungen",
    8: "diagnose",
    9: "rohdaten",
    10: "gute_schwelle",
    11: "auftrag",
    12: "aus",
    13: "abschaltbar",
  },
  meldung: {
    1: "art",
    2: "domain",
    3: "type",
    4: "severity",
    5: "entity",
    6: "summary",
    7: "zeit",
  },
  "feinstaub.luft": {
    1: "pm1_0",
    2: "pm2_5",
    3: "pm4_0",
    4: "pm10_0",
    5: "partikel_0_5",
    6: "partikel_2_5",
    7: "partikelgroesse",
  },
  "druckwaechter.stand": {
    1: "modus",
    2: "zustand",
    3: "grund",
    4: "seit",
    5: "datei",
    6: "stufe",
    7: "wert",
    8: "glaettung",
    9: "verlauf",
    10: "schonzeit",
    11: "stumm",
    12: "bilder_je_s",
    13: "erkenner_ms",
    14: "erkenner",
    15: "takt_s",
    16: "sicht",
    17: "fragt",
    18: "letzte_frage",
    19: "offener_fall",
    20: "letzter_fall",
    21: "einstellung",
  },
  "erkenner.zustand": {
    1: "bereit",
    2: "modell",
    3: "threads",
    4: "gestartet",
    5: "bilder",
    6: "ms_letztes",
    7: "ms_mittel",
    8: "letzte_anfrage",
    9: "fehler",
  },
  "task.lauf": {
    1: "run",
    2: "status",
    3: "start",
    4: "ende",
    5: "dauer_ms",
    6: "fehler",
    7: "naechster",
    8: "typische_dauer_ms",
    9: "fortschritt",
  },
  "geraet.stand": {
    1: "name",
    2: "label",
    3: "bereich",
    4: "art",
    5: "hersteller",
    6: "modell",
    7: "integration",
    8: "integration_slug",
    9: "adresse",
    10: "online",
    11: "zuletzt",
    12: "lqi",
    13: "stilllegung_domain",
    14: "stilllegung_id",
    15: "versorgt",
    16: "aus_gesperrt",
    17: "funkmelder",
    18: "entitaeten",
  },
  "speicher.pool": {
    1: "host",
    2: "host_id",
    3: "cluster",
    4: "name",
    5: "groesse",
    6: "belegt",
    7: "frei",
    8: "frag",
    9: "zustand",
    10: "nutzbar_groesse",
    11: "nutzbar_belegt",
    12: "nutzbar_frei",
    13: "overhead",
    14: "mitglieder",
    15: "scan",
    16: "status_text",
    17: "action_text",
    18: "ersatz",
  },
  "speicher.platte": {
    1: "host",
    2: "host_id",
    3: "cluster",
    4: "name",
    5: "groesse",
    6: "rotierend",
    7: "anschluss",
    8: "modell",
    9: "zustand",
    10: "temp",
    11: "pool",
    12: "nvme",
    13: "seriennummer",
    14: "firmware",
    15: "betriebsstunden",
    16: "geschrieben",
    17: "verschleiss",
    18: "umgelagert",
    19: "schwebend",
    20: "offline_unkorrigierbar",
    21: "gemeldet_unkorrigierbar",
    22: "crc_fehler",
    23: "selbsttest_status",
    24: "selbsttest_ok",
    25: "selbsttest_vor_h",
    26: "selbsttest_abbruch",
    27: "selbsttest_abbruch_vor_h",
    28: "selbsttest_laeuft",
    29: "selbsttest_rest",
    30: "prefail",
    31: "partitionen",
    32: "signaturen",
    33: "belegt",
  },
  "speicher.dataset": {
    1: "host",
    2: "cluster",
    3: "name",
    4: "belegt",
    5: "verfuegbar",
    6: "referenziert",
    7: "mountpoint",
  },
  "speicher.dateisystem": {
    1: "host",
    2: "cluster",
    3: "quelle",
    4: "mountpoint",
    5: "groesse",
    6: "belegt",
    7: "verfuegbar",
  },
  "speicher.lage": {
    1: "zuletzt",
    2: "ok",
    3: "warn_pct",
    4: "krit_pct",
  },
  "licht.bad": {
    1: "name",
    2: "aktiv",
    3: "an",
    4: "manuell",
    5: "quelle",
    6: "lampe",
    7: "phase",
    8: "phase_label",
    9: "phasen",
    10: "sonnenaufgang",
    11: "sonnenuntergang",
    12: "naechste_phase",
    13: "naechste_phase_um",
    14: "abend_ende",
    15: "bewegung_topic",
    16: "tuer_topic",
    17: "dimmer_topic",
    18: "schalter_topics",
    19: "bewegung_frist",
    20: "tuer_verzug",
    21: "lernen",
    22: "lernen_folgen",
    23: "lern_anzahl",
    24: "lern_proben",
    25: "lern_kurve",
    26: "lern_abschnitte",
    27: "jetzt_helligkeit",
    28: "jetzt_sicher",
    29: "jetzt_vorlaeufig",
    30: "wunsch",
  },
  "licht.ambient": {
    1: "name",
    2: "aktiv",
    3: "an",
    4: "phase",
    5: "phase_label",
    6: "phasen",
    7: "szene",
    8: "sonnenaufgang",
    9: "sonnenuntergang",
    10: "naechste_phase",
    11: "naechste_phase_um",
    12: "ziele",
    13: "uebergang",
    14: "uebergang_standard",
    15: "uebergang_max",
    16: "abend_ende",
    17: "lernen",
    18: "lernen_folgen",
    19: "lern_anzahl",
    20: "lern_proben",
    21: "lern_kurve",
    22: "lern_abschnitte",
    23: "jetzt_helligkeit",
    24: "jetzt_sicher",
    25: "jetzt_vorlaeufig",
    26: "wunsch",
  },
  "licht.szene": {
    1: "name",
    2: "aktiv",
    3: "an",
    4: "go_engine",
    5: "ambient_zurueck",
    6: "ziel_anzahl",
    7: "farben",
    8: "ziele",
  },
  "agent.stand": {
    1: "name",
    2: "art",
    3: "mqtt_topic",
    4: "lebenszeichen_s",
    5: "daten_s",
    6: "max_stille_s",
    7: "ssh_alias",
    8: "aktiv",
    9: "notiz",
    10: "angelegt",
    11: "geaendert",
    12: "status",
    13: "zuletzt",
    14: "abgemeldet_seit",
    15: "deploy",
    16: "gemessen_s",
    17: "proben",
    18: "abweichung",
    19: "soll_s",
  },
  "agent.kandidat": {
    1: "topic",
    2: "art",
    3: "name",
    4: "frames",
    5: "erstmals",
    6: "zuletzt",
  },
  "steckdose.stand": {
    1: "quelle",
    2: "name",
    3: "knoten",
    4: "schalter",
    5: "hat_schalter",
    6: "watt",
    7: "kwh",
    8: "volt",
    9: "ampere",
    10: "online",
    11: "kwh_gesamt",
    12: "zuletzt",
    13: "gemessen",
  },
  "growbox.stand": {
    1: "name",
    2: "mac",
    3: "online",
    4: "temperatur",
    5: "feuchte",
    6: "vpd",
    7: "ports",
    8: "phase",
    9: "position",
  },
  "growbox.lage": {
    1: "eingerichtet",
    2: "angemeldet",
    3: "phasen",
    4: "letzter_abruf",
    5: "email",
  },
  "sensor.stand": {
    1: "quelle",
    2: "name",
    3: "temperatur",
    4: "feuchte",
    5: "druck",
    6: "batterie",
    7: "funk",
    8: "bewegung",
    9: "kontakt",
    10: "kontakt_seit",
    11: "wasser",
    12: "status",
    13: "status_zeit",
    14: "werte",
    15: "zuletzt",
  },
  "klima.bewertung": {
    1: "domain",
    2: "sensor",
    3: "zeit",
    4: "werte",
    5: "batterie",
    6: "status",
    7: "grund",
    8: "aus",
    9: "seit",
    10: "aussen",
    11: "warn_nach",
  },
  "netz.cluster": {
    1: "id",
    2: "name",
    3: "subnetz",
    4: "hosts",
    5: "geraete",
    6: "position",
  },
  "openwrt.stand": {
    1: "router",
    2: "host",
    3: "topic",
    4: "wan",
    5: "empfang",
    6: "senden",
    7: "tunnel",
    8: "tailscale",
    9: "zuletzt",
  },
  "link.stand": {
    1: "entity",
    2: "host",
    3: "iface",
    4: "zustand",
    5: "gesundheit",
    6: "seit",
    7: "ausfall_seit",
    8: "ausfall",
    9: "flaps",
    10: "letzter_flap",
    11: "letztes_ereignis",
  },
  "topologie.knoten": {
    1: "id",
    2: "label",
    3: "art",
    4: "eltern",
    5: "cluster",
    6: "rolle",
    7: "ip",
    8: "os",
    9: "status",
    10: "link",
    11: "pruefungen",
    12: "position",
  },
  "topologie.kanten": {
    1: "kanten",
    2: "pruefkanten",
  },
  "netz.lage": {
    1: "rekey",
    2: "integrationen",
  },
  "wan.lage": {
    1: "ok",
    2: "status",
    3: "fehlversuche",
    4: "geprueft",
    5: "erfolg",
    6: "fehlschlag",
    7: "fehler",
    8: "ziel",
    9: "seit",
    10: "ipv4",
    11: "ipv6",
    12: "empfang",
    13: "senden",
    14: "router",
    15: "routers",
  },
  "host.stand": {
    1: "id",
    2: "name",
    3: "ip",
    4: "mac",
    5: "rolle",
    6: "os",
    7: "schlafen_ok",
    8: "temp_grenze",
    9: "neustart_prio",
    10: "neustart_tag",
    11: "neustart_nach",
    12: "quelle",
    13: "online",
    14: "seit",
    15: "offline_24h",
    16: "updates",
    17: "ssh_ok",
    18: "ssh_fehler",
    19: "ssh_adresse",
    20: "noch_nicht_in_betrieb",
    21: "aufenthalt",
    22: "cluster",
    23: "position",
  },
  "host.lage": {
    1: "cluster",
    2: "anomalien",
    3: "offline_ok",
    4: "verlauf_host",
  },
  "aktor.stand": {
    1: "name",
    2: "art",
    3: "zustand",
    4: "helligkeit",
    5: "farbtemperatur",
    6: "farbe",
    7: "zuletzt",
    8: "erreichbar",
    9: "batterie",
    10: "funk",
  },
  "docker.stand": {
    1: "rechner",
    2: "name",
    3: "image",
    4: "status",
    5: "zustand",
    6: "ports",
    7: "stack",
    8: "cpu",
    9: "speicher",
    10: "speicher_grenze",
    11: "speicher_anteil",
    12: "gpu",
    13: "position",
  },
  "docker.lage": {
    1: "rechner",
  },
  "argus.teil": {
    1: "json",
  },
  "argus.lauf": {
    1: "json",
  },
  "argus.gross": {
    1: "json",
  },
  "uptime.stand": {
    1: "entity",
    2: "ziel_id",
    3: "bezeichnung",
    4: "blickwinkel",
    5: "url",
    6: "zustand",
    7: "ok",
    8: "http_status",
    9: "latenz",
    10: "seit",
    11: "fehlversuche",
    12: "wackler",
    13: "geprueft",
    14: "fehler",
    15: "zertifikat_bis",
    16: "zertifikat_tage",
    17: "position",
  },
  "dns.lage": {
    1: "ok",
    2: "zustand",
    3: "server",
    4: "modus",
    5: "seit",
    6: "wackler",
    7: "latenz",
  },
  "bypass.lage": {
    1: "an",
    2: "auto_aus_min",
    3: "an_seit",
  },
  "claude.lage": {
    1: "json",
  },
  "claude.chat": {
    1: "json",
  },
  "claude.commits": {
    1: "json",
  },
  "claude.repo": {
    1: "json",
  },
  AgentDeploy: {
    1: "status",
    2: "versuch",
    3: "erfolg",
    4: "fehler",
    5: "versuche",
    6: "unerreichbar_seit",
  },
  AmbientPhase: {
    1: "schluessel",
    2: "label",
    3: "voreinstellungen",
    4: "gewaehlt",
    5: "helligkeit",
    6: "standard_helligkeit",
    7: "helligkeit_eigen",
    8: "start",
    9: "start_stunde",
  },
  BadPhase: {
    1: "schluessel",
    2: "label",
    3: "helligkeit",
    4: "standard_helligkeit",
    5: "helligkeit_eigen",
    6: "farbtemp",
    7: "standard_farbtemp",
    8: "farbtemp_eigen",
    9: "uebergang",
    10: "start",
    11: "start_stunde",
  },
  DockerGpu: {
    1: "prozent",
    2: "speicher",
    3: "motoren",
  },
  DockerMotor: {
    1: "name",
    2: "prozent",
  },
  DockerRechner: {
    1: "id",
    2: "name",
    3: "online",
  },
  ErkennerStelle: {
    1: "p",
    2: "x1",
    3: "y1",
    4: "x2",
    5: "y2",
  },
  FunkDiagnose: {
    1: "am",
    2: "erzeugt",
    3: "gesendet",
    4: "empfangen",
    5: "rueckrufe",
    6: "angenommen",
    7: "verwertet",
    8: "verworfen",
    9: "fehlend",
    10: "ueberzaehlig",
    11: "veraltet",
    12: "unsortiert",
    13: "belegung",
    14: "kanal",
    15: "signal",
    16: "befund",
  },
  FunkEinstellungen: {
    1: "erkennung",
    2: "treffer_an",
    3: "treffer_aus",
    4: "haltezeit",
    5: "led",
  },
  FunkRolle: {
    1: "id",
    2: "aus",
    3: "abschaltbar",
  },
  GeraetEntitaet: {
    1: "slug",
    2: "name",
    3: "art",
    4: "einheit",
    5: "zustand",
    6: "klasse",
    7: "ext_id",
    8: "aktualisiert",
  },
  GrowboxPort: {
    1: "port",
    2: "name",
    3: "art",
    4: "stufe",
    5: "stufe_an",
    6: "stufe_aus",
    7: "an",
    8: "modus",
    9: "ueberschuss",
  },
  GuteSchwelle: {
    1: "schwelle",
    2: "quelle",
    3: "am",
  },
  HostAnomalie: {
    1: "host",
    2: "metrik",
    3: "aktuell",
    4: "mittel",
    5: "z",
  },
  HostAufenthalt: {
    1: "adresse",
    2: "eingetragen",
    3: "vom_agenten",
    4: "ort",
    5: "netz",
    6: "gemeldet",
  },
  HostCluster: {
    1: "id",
    2: "name",
  },
  HostUpdates: {
    1: "offen",
    2: "zurueckgehalten",
    3: "sicherheit",
    4: "neustart",
    5: "uu_aktiv",
    6: "apt_zuletzt",
    7: "zeit",
  },
  Integration: {
    1: "id",
    2: "name",
    3: "status",
    4: "detail",
    5: "zuletzt",
    6: "geraete",
    7: "geraete_online",
    8: "gesamt",
  },
  KalibrierAuftrag: {
    1: "seit",
    2: "bis",
    3: "laeuft",
    4: "grund",
  },
  LaufFortschritt: {
    1: "bytes",
    2: "pct",
    3: "speed",
    4: "eta",
    5: "aktualisiert",
  },
  LernAbschnitt: {
    1: "von_min",
    2: "bis_min",
    3: "helligkeit",
  },
  LernKurvenPunkt: {
    1: "minute",
    2: "helligkeit",
    3: "sicher",
    4: "gefuellt",
    5: "vorlaeufig",
  },
  LernPunkt: {
    1: "minute",
    2: "helligkeit",
    3: "bestaetigt",
  },
  LichtGrenze: {
    1: "sonne",
    2: "versatz_min",
    3: "uhrzeit",
  },
  LichtVoreinstellung: {
    1: "name",
    2: "farben",
  },
  LichtZiel: {
    1: "topic",
    2: "typ",
  },
  NetzGeraet: {
    1: "name",
    2: "ip",
    3: "mac",
    4: "rolle",
    5: "verbunden_mit",
    6: "online",
  },
  NetzHost: {
    1: "id",
    2: "name",
    3: "ip",
    4: "mac",
    5: "rolle",
    6: "schlafen_ok",
    7: "os",
    8: "temp_grenze",
    9: "online",
    10: "noch_nicht_in_betrieb",
  },
  OpenwrtTailscale: {
    1: "online",
    2: "ipv4",
    3: "backend",
    4: "empfang",
    5: "senden",
  },
  OpenwrtTunnel: {
    1: "name",
    2: "oben",
    3: "laufzeit",
    4: "ipv4",
    5: "ipv6",
  },
  RekeyFenster: {
    1: "host",
    2: "bis",
    3: "geoeffnet",
    4: "von",
    5: "grund",
  },
  SensorWert: {
    1: "name",
    2: "wert",
    3: "zeit",
  },
  SpeicherErsatz: {
    1: "platte",
    2: "seriennummer",
    3: "modell",
    4: "groesse",
    5: "hat_daten",
    6: "partitionen",
    7: "signaturen",
    8: "mitglied",
    9: "mitglied_label",
    10: "smart_warnung",
  },
  SpeicherMitglied: {
    1: "name",
    2: "pfad",
    3: "guid",
    4: "zustand",
    5: "lesefehler",
    6: "schreibfehler",
    7: "pruefsummenfehler",
    8: "gruppe",
    9: "resilver",
    10: "notiz",
    11: "platte",
    12: "seriennummer",
    13: "label",
  },
  SpeicherScan: {
    1: "art",
    2: "laeuft",
    3: "prozent",
    4: "rest",
    5: "tempo",
    6: "menge",
    7: "text",
  },
  Strom: {
    1: "throttled_now",
    2: "throttled_seen",
    3: "volt_core",
    4: "throttled_hex",
  },
  SzenenZiel: {
    1: "topic",
    2: "typ",
    3: "payload",
  },
  TopoKante: {
    1: "id",
    2: "quelle",
    3: "ziel",
    4: "art",
    5: "label",
  },
  TopoLink: {
    1: "gesundheit",
    2: "iface",
    3: "flaps",
    4: "letzter_flap",
    5: "ausfall",
  },
  TopoPruefkante: {
    1: "id",
    2: "quelle",
    3: "ziel",
    4: "blick",
    5: "gesundheit",
    6: "label",
  },
  TopoPruefung: {
    1: "blick",
    2: "gesundheit",
    3: "http_status",
    4: "latenz",
    5: "url",
    6: "notiz",
    7: "zuletzt",
  },
  WaechterEinstellung: {
    1: "modus",
    2: "empfindlichkeit",
    3: "schwelle_niedrig",
    4: "schwelle_hoch",
    5: "steigerung",
    6: "schonzeit_min",
    7: "ohne_meinung",
    8: "modell",
  },
  WaechterGlaettung: {
    1: "ewm",
    2: "kurz",
    3: "lang",
    4: "takte",
    5: "takte_gesamt",
    6: "p",
  },
  WaechterMeinung: {
    1: "urteil",
    2: "art",
    3: "begruendung",
    4: "modell",
    5: "ms",
    6: "fehler",
    7: "ausschnitt",
  },
  WaechterSicht: {
    1: "zeit",
    2: "stellen",
    3: "breite",
    4: "hoehe",
    5: "p",
    6: "bewertet",
    7: "hinweis",
  },
  WanRouter: {
    1: "host",
    2: "name",
    3: "cluster",
    4: "cluster_name",
    5: "quelle",
    6: "status",
    7: "seit",
    8: "zuletzt",
    9: "stumm",
    10: "wan_oben",
    11: "ipv4",
    12: "ipv6",
    13: "empfang",
    14: "senden",
  },
  Wlan: {
    1: "clients",
    2: "bands",
  },
  WlanBandZahl: {
    1: "band",
    2: "clients",
  },
} as const;
